import React, { useState, useRef, useEffect } from 'react'
import { optionMatches } from 'helpers/search'
import Input from 'components/input'
import Menu, { MenuProps } from 'components/menu'
import FloatingMenu from 'components/menu/floating'
import { cancelTimeout, requestTimeout, TimeoutID } from 'helpers/timer'
import { updateColumn } from 'components/spreadsheet/contexts/data/actions'
import { useColumnRename } from 'components/spreadsheet/hooks/useColumnRename'
import { getApiErrorMessage } from 'helpers/apiError'
import { useApplicationStore } from 'hooks/application'
import ColumnDescriptionField from 'components/spreadsheet/components/column/description-field'
import {
  ALL_TYPES,
  SELECT_COLUMN_TYPES,
  BLOCK_SELECT_OPTION_TYPES,
  BLOCK_GROUP_BY,
  COLUMN_TYPE_NAMES,
  DATE_COLUMN_TYPES,
  NUMBER_COLUMN_TYPES,
  VOTE_COLUMN_TYPES,
  ATTACHMENT_COLUMN_TYPES,
  DEFAULT_KIND_OPTIONS,
  GROUP_KEY_SPLIT
} from 'components/spreadsheet/constants/const'
import { IColumnTypes } from 'components/spreadsheet/types'
import {
  ITableViewWithColumns,
  SelectKindOptions,
  ITableViewColumn,
  IContextMenuState,
  SelectDependencyColumn,
  EditorContent
} from 'types'
import {
  ErrorOutline,
  Expand,
  Cross,
  Triangle,
  Code,
  Grid,
  ExternalLink,
  Plus,
  Search,
  ChevronDown,
  Pencil,
  Dependency,
  SortAZ,
  SortZA,
  ManualList,
  UpdateSource,
  Warning,
  SyncValues,
  SyncValuesOff
} from 'components/icons'
import Tooltip from 'components/tooltip'
import { Link, generatePath } from 'react-router-dom'
import { useProject } from 'hooks/useProject'
import api from 'helpers/api'
import IconSelector from 'components/spreadsheet/components/cell/components/icon'
import ColumnKindModal from 'components/spreadsheet/components/modal/views/columnkind'
import {
  isEditorEmpty,
  getDefaultValueError,
  kindOptionsToSave,
  sourceValuesOf
} from 'components/spreadsheet/helpers/functions'
import { toUTCString, formatDate, formatDateTime } from 'components/spreadsheet/helpers/format'
import Button from 'components/button'
import { useDataContext } from 'components/spreadsheet/contexts/data/useDataContext'
import { getFilterValues } from 'components/spreadsheet/helpers/filtering'
import Select from 'components/select'
import Field from 'components/field'
import Dialog, { DIALOG_ASIDE_FOOTER } from 'components/dialog'
import ActionDialog from 'components/dialog/action'
import Empty from 'components/empty'
import SwitchField from 'components/switch-field'
import HoverCard from 'components/hover-card'
import { MenuItem, MenuList, MenuSearch, MenuSeparator } from 'components/menu/items'
import { PanelAction, PanelAdd, PanelFooter, PanelRule, PanelRules, PanelSegmentSelect } from 'components/menu/panel'
import { INITIAL_CONTEXT_MENU_STATE, OVER_PANEL_Z_INDEX } from 'app-constants'
import { BLUE_BUTTON } from 'components/save-buttons'
import ColumnManualOptions, { isAscending, ViewAllPill } from 'components/spreadsheet/components/column/manual-options'
import ColumnValidationFields, {
  ColumnValidationValue
} from 'components/spreadsheet/components/column/validation-fields'
import {
  ColumnTypeFormattingFields,
  ColumnFormattingValue
} from 'components/spreadsheet/components/column/formatting-fields'
import SelectSourceFields from 'components/spreadsheet/components/column/select-source-fields'
import { ColumnDraft } from 'components/spreadsheet/components/new-column/useNewColumn'
import Formula, { DEFAULT_FORMULA } from './components/formula'
import Script from './components/script'
import { TABLE } from 'routes'
import Spinner from 'components/spinner'
import { tv } from 'tailwind-variants'
import { ColumnName, openLookups } from 'components/spreadsheet/components/modal/views/tablejoins'
import LookupSample from 'components/spreadsheet/components/column/lookup-sample'
import { useColumnLookup } from 'components/spreadsheet/components/header/LookupIndicator'

// Structured / widget kinds whose value is built through a modal (files, votes, link
// lists) rather than typed in — a user-entered default is meaningless for them and the
// backend rejects almost any string, so we don't show the Default Value field at all.
const DEFAULT_VALUE_UNSUPPORTED_KINDS: IColumnTypes[] = [...VOTE_COLUMN_TYPES, ...ATTACHMENT_COLUMN_TYPES, 'multilink']

const EDIT_COLUMN_MENU_WIDTH = 360
// The formula and script editors, which need the room
const SUB_PANEL_WIDTH = 560
// How many of a sourced list's options the source dialog lists at once: enough to check it, never all of a
// source with tens of thousands of rows.
const LISTED_SOURCE_OPTIONS = 100

const panel = tv({
  base: 'flex flex-col',
  variants: {
    view: {
      settings: 'max-h-[80vh]',
      subPanel: 'h-[460px] max-h-[80vh]',
      fullScreen: 'h-full'
    }
  }
})

// The editors that need room, and can go full screen; the others are pages of the panel itself.
const WIDE_SUB_PANELS: SubPanel[] = ['formula', 'script']

const panelView = (subPanel: SubPanel | null, fullScreen: boolean): 'settings' | 'subPanel' | 'fullScreen' => {
  if (!subPanel || !WIDE_SUB_PANELS.includes(subPanel)) return 'settings'
  return fullScreen ? 'fullScreen' : 'subPanel'
}

const SUB_PANEL_TITLES: Record<SubPanel, string> = {
  formula: 'Formula',
  script: 'Script',
  options: 'Options'
}

const SOURCE_MENU_WIDTH = 220

// A column named in a rule pill but not picked there. Medium, as the source table button over the rules is,
// rather than the semibold of the toolbar's filters: the names in a rule sit a level under the source.
// Its own width, never squeezed (a fraction of a pixel short ends a name in an ellipsis), up to 160px, past
// which it's cut short: two names that long, "matches" and the × still fit the dialog's line.
const STATIC_FIELD_SEGMENT =
  'flex h-full min-w-0 max-w-[160px] shrink-0 items-center gap-1 px-1.5 text-[13px] font-medium whitespace-nowrap text-body [&_svg]:size-3.5 [&_svg]:shrink-0 select-none'

// How many of a list's options the panel shows before it fades out.
const PREVIEWED_OPTIONS = 5

// A field that is typed into rather than clicked waits for a pause in typing before it saves, so a
// value isn't sent per keystroke. Everything else (checkboxes, dropdowns) saves on change.
const SAVE_DEBOUNCE_MS = 1000

// Everything the shared validation/formatting components can patch. Each key is also an `updateColumn`
// field name, which is what lets a patch be routed straight to the API without a translation table.
type ColumnConfigValue = ColumnValidationValue & ColumnFormattingValue
type ColumnConfigKey = keyof ColumnConfigValue

// Every field of the panel that saves after a pause in typing rather than on change. The validation and format
// settings save at once: they're picked from lists and switches, or typed in a popover and saved with its Save.
type PendingSaveKey = 'manualOptions' | 'description' | 'defaultValue'

// Which page of the panel is open, if any.
type SubPanel = 'formula' | 'script' | 'options'

// The column's saved validation + formatting settings, in the shape the shared field components expect.
const columnConfigValue = (column?: ITableViewColumn): ColumnConfigValue => ({
  stringValidation: column?.stringValidation ?? null,
  validationMessage: column?.validationMessage ?? null,
  validationNoDuplicates: column?.validationNoDuplicates ?? false,
  validationNoBlanks: column?.validationNoBlanks ?? false,
  hardValidation: column?.hardValidation ?? false,
  blockInvalidSelectValues: column?.blockInvalidSelectValues ?? false,
  displayValidationError: column?.displayValidationError ?? false,
  thousandSeparator: column?.thousandSeparator ?? false,
  decimalPlaces: column?.decimalPlaces ?? 2,
  dateFormat: column?.dateFormat ?? null,
  displayLink: column?.displayLink ?? false
})

// The browser field a default value is typed or picked in, for the column's kind.
const defaultValueInputType = (kind: IColumnTypes): string => {
  if (kind === 'datetime') return 'datetime-local'
  if (kind === 'date') return 'date'
  if (NUMBER_COLUMN_TYPES.includes(kind)) return 'number'
  return 'text'
}

// Stored ISO8601 default -> the value an <input type="date|datetime-local"> expects.
const formatDefaultDateForInput = (value: string | null | undefined, kind: IColumnTypes): string => {
  if (!value) return ''
  return kind === 'datetime' ? formatDateTime(value + 'Z', 'YYYY-MM-DDTHH:mm:ss') : formatDate(value, 'YYYY-MM-DD')
}

// A sourced list's options, each with what it's offered for. A select with dependency columns has its
// options grouped by the dependency's value ({ France: ['Lyon', 'Nice'], UK: […] }) rather than listed.
const sourcedOptionsOf = (
  cached: string[] | { [key: string]: string[] } | undefined
): Array<{ value: string; group?: string }> =>
  Array.isArray(cached)
    ? cached.map((value) => ({ value }))
    : Object.entries(cached ?? {}).flatMap(([group, values]) => (values ?? []).map((value) => ({ value, group })))

// Where a select column's options come from: a hand-entered list, or a source table column.
type SelectOptionsMode = 'manual' | 'table'

// A linked column whose source column has nothing in it yet, wherever its options are listed.
const NoSourceOptions: React.FC<{ className?: string; children?: React.ReactNode; 'data-testid'?: string }> = ({
  children,
  ...props
}) => (
  <Empty
    {...props}
    icon={<Grid />}
    title="No options in source table"
    description="The source column has no values yet. Values added there show here as options."
  >
    {children}
  </Empty>
)

// A source's options still on their way, wherever its options are listed.
const LoadingSourceOptions: React.FC<{ className?: string }> = ({ className }) => (
  <Empty
    className={className}
    icon={<Spinner className="h-4 w-4 border-2" />}
    title="Loading options"
    description="The source column's values are loading. They show here as options once they've loaded."
    data-testid="options-source-loading"
  />
)

// A search of the source's options that finds none.
const noOptionsMatch = <Empty icon={<Search />} title="No options match" description="Try another search." />

// A dependency not chosen yet: the Add dependency picker's state while nothing is picked in it.
const NO_DEPENDENCY: SelectDependencyColumn = { columnId: '', columnJoinId: '' }

// Auto-select acts only on dependencies: when a change leaves none (the last removed, the link removed, another
// table picked), it goes off with them rather than staying on out of sight.
const settleAutoSelect = (previous: SelectKindOptions, next: SelectKindOptions): SelectKindOptions => {
  const lost =
    (previous.tableOptions.dependencies?.length ?? 0) > 0 || previous.tableOptions.tableId !== next.tableOptions.tableId
  return lost && !(next.tableOptions.dependencies?.length ?? 0) ? { ...next, autopopulate: false } : next
}

// A new column's draft as the panel reads a column: a stand-in id, and a select's options at their defaults.
const newColumnFromDraft = (draft: ColumnDraft): ITableViewColumn =>
  ({
    ...draft,
    publicId: NEW_COLUMN_ID,
    isSynced: false,
    kindOptions: DEFAULT_KIND_OPTIONS
  }) as ITableViewColumn

// A list of options in its groups, in the order they come: [{ group: 'France', options: ['Lyon', …] }, …].
const groupedOptions = (list: Array<{ value: string; group?: string }>): Array<{ group?: string; options: string[] }> =>
  list.reduce<Array<{ group?: string; options: string[] }>>((groups, option) => {
    const last = groups[groups.length - 1]
    if (last && last.group === option.group) last.options.push(option.value)
    else groups.push({ group: option.group, options: [option.value] })
    return groups
  }, [])

// "1 option", "17 options", "39,949 options".
const optionCount = (count: number): string => `${count.toLocaleString()} ${count === 1 ? 'option' : 'options'}`

// Object spread only fills in *missing* keys, so an explicit `null` from the API
// (e.g. a select column whose stored manual_options is NULL) would overwrite the
// array defaults and crash consumers that call `.length`. Coerce those nulls back
// to their empty-list defaults here so the rest of the component can assume arrays.
const mergeKindOptions = (kindOptions?: SelectKindOptions): SelectKindOptions => {
  const merged = {
    ...DEFAULT_KIND_OPTIONS,
    ...(kindOptions ?? {}),
    tableOptions: {
      ...DEFAULT_KIND_OPTIONS.tableOptions,
      ...(kindOptions?.tableOptions ?? {})
    }
  }
  return {
    ...merged,
    manualOptions: merged.manualOptions ?? [],
    tableOptions: {
      ...merged.tableOptions,
      cachedOptions: merged.tableOptions.cachedOptions ?? [],
      dependencies: merged.tableOptions.dependencies ?? []
    }
  }
}

// A column not created yet, set up in the same panel: its settings are kept as a draft, and the column is
// created with them on Add column. Nothing is saved before then, and closing the panel creates nothing.
export interface NewColumnProps {
  // The column as it would be created with its type's defaults (see buildNewColumn).
  draft: ColumnDraft
  // Create the column as it's been set up here.
  onCreate: (column: ITableViewColumn) => void
  // Close without creating it.
  onCancel: () => void
  creating: boolean
}

// What stands in for a new column's id until it's created: no saved column has it.
export const NEW_COLUMN_ID = 'new-column'

interface EditColumnMenuProps extends MenuProps {
  newColumn?: NewColumnProps
}

const EditColumnMenu: React.FC<EditColumnMenuProps> = ({ id, menuState, setMenuState, width, newColumn }) => {
  const { displayErrorMessage, setSnackbarMessage } = useApplicationStore()
  const { spreadsheetData, setSpreadsheetData } = useDataContext()

  // A new column's settings as they're set. Held in a ref too, so Add column reads every change, even one
  // made in the same moment (a name committed as its field blurs, a typed setting flushed on the way out).
  const isNew = !!newColumn
  const [draftColumn, setDraftColumn] = useState<ITableViewColumn | undefined>(() =>
    newColumn ? newColumnFromDraft(newColumn.draft) : undefined
  )
  const draftRef = useRef<ITableViewColumn | undefined>(draftColumn)
  const column = isNew
    ? draftColumn
    : spreadsheetData.viewDetails.columns.find((col) => col.publicId === menuState.columnId)

  const { publicId, description, kind, script, scriptEnabled, formula, formulaEnabled, isJoined } = column || {}
  // The lookup filling a lookup column, with its source's name, for its Lookup section.
  const columnLookup = useColumnLookup(publicId, !!isJoined && menuState.open)
  const openColumnLookups = (): void => {
    handleClose()
    setMenuState(INITIAL_CONTEXT_MENU_STATE)
    openLookups({ tableId: spreadsheetData.tableDetails.publicId, joinId: columnLookup?.publicId })
  }
  const [formulaHasChanges, setFormulaHasChanges] = useState<boolean>(false)
  const [selectedKind, setSelectedKind] = useState<IColumnTypes>()
  const [processing, setProcessing] = useState<boolean>(false)
  const [changeColumnKindModalState, setChangeColumnKindModalState] = useState<boolean>(false)
  const [dependencyColumn, setDependencyColumn] = useState<SelectDependencyColumn>(NO_DEPENDENCY)
  // Which editor the menu has drilled into. Null while the settings fill the whole panel.
  const [subPanel, setSubPanel] = useState<SubPanel | null>(menuState.columnPanel ?? null)
  const [subPanelFullScreen, setSubPanelFullScreen] = useState<boolean>(false)
  // The dialog where a select's options are sourced from another table, and its options search.
  const [sourceDialogOpen, setSourceDialogOpen] = useState<boolean>(false)
  // Whether the source dialog was opened on a column already linked, so its title stays put while a table is
  // picked in it: Update source then, Link a table otherwise.
  const [sourceDialogLinked, setSourceDialogLinked] = useState<boolean>(false)
  // The source picked in its own small dialog over the source dialog, kept as a draft until it's saved: the
  // table, view and column, and the draft view as fetched (for its columns).
  const [sourcePickerOpen, setSourcePickerOpen] = useState<boolean>(false)
  const [sourceDraft, setSourceDraft] = useState<{
    tableId: string
    viewId: string
    columnId: string
    liveValues: boolean
  }>({ tableId: '', viewId: '', columnId: '', liveValues: false })
  const [draftSourceView, setDraftSourceView] = useState<ITableViewWithColumns>()
  const [optionSearch, setOptionSearch] = useState<string>('')
  // Whether a linked column's full list shows its own values that are missing from the source table, opened
  // from the banner over the list, rather than the source's options.
  const [missingShown, setMissingShown] = useState<boolean>(false)
  // In the missing options and the source dialog's preview, the search waits behind an icon until it's wanted.
  const [searchOpen, setSearchOpen] = useState<boolean>(false)
  const collapsedSearchRef = useRef<HTMLInputElement>(null)
  // Opened from its icon, it takes the caret, as a search does.
  useEffect(() => {
    if (searchOpen) collapsedSearchRef.current?.focus()
  }, [searchOpen])
  // The menu on a table source's row.
  const [sourceMenu, setSourceMenu] = useState<IContextMenuState>(INITIAL_CONTEXT_MENU_STATE)
  // Whether the default value shows: as the description does, behind "Add default value" until there's one.
  const [defaultShown, setDefaultShown] = useState<boolean>(!!column?.defaultValue)
  // A typed default value as it's typed (a date in the form its input takes), while its save waits.
  const [defaultDraft, setDefaultDraft] = useState<string>(
    column && DATE_COLUMN_TYPES.includes(column.kind)
      ? formatDefaultDateForInput(column.defaultValue, column.kind)
      : (column?.defaultValue ?? '')
  )

  // Select properties
  const { project } = useProject()
  const [selectedSourceView, setSelectedSourceView] = useState<ITableViewWithColumns>()
  const [currentKindOptions, setCurrentKindOptions] = useState<SelectKindOptions>(mergeKindOptions(column?.kindOptions))
  const [optionsMode, setOptionsMode] = useState<SelectOptionsMode>(
    column?.kindOptions?.tableOptions?.tableId ? 'table' : 'manual'
  )

  // Loaders
  const [sourceRestricted, setSourceRestricted] = useState<boolean>(false)
  // While a source just picked is still being read for its options (a large one can take a while): shown as loading,
  // not as a source with none (#11452).
  const [sourceOptionsLoading, setSourceOptionsLoading] = useState<boolean>(false)

  // Edits the user has made that the API hasn't echoed back yet. Rendering the saved column merged with
  // these keeps every control showing the change while its save is on its way, and a key drops out
  // again the moment the server confirms it — so a change made anywhere else still reaches the panel.
  const [pendingConfigPatch, setPendingConfigPatch] = useState<Partial<ColumnConfigValue>>({})
  const configValue: ColumnConfigValue = { ...columnConfigValue(column), ...pendingConfigPatch }

  // Debounced saves that haven't fired yet, keyed by field so a second edit to the same field replaces
  // the first, and so closing the panel can flush them all rather than dropping them. The typed fields
  // debounce through here: the description, the default value and a select column's manual option list. The
  // validation and formatting settings are picked or saved from a popover, so they're sent at once.
  const pendingSaves = useRef<Map<PendingSaveKey, { timeout: TimeoutID; save: () => void }>>(new Map())

  // Drop a save still waiting for this field — it's about to be replaced, or sent straight away.
  const cancelPendingSave = (key: PendingSaveKey): void => {
    const existing = pendingSaves.current.get(key)
    if (existing) cancelTimeout(existing.timeout)
    pendingSaves.current.delete(key)
  }

  // Queue a save for when typing pauses, replacing one still waiting for the same field.
  const debounceSave = (key: PendingSaveKey, save: () => void): void => {
    cancelPendingSave(key)
    pendingSaves.current.set(key, {
      save,
      timeout: requestTimeout(() => {
        pendingSaves.current.delete(key)
        save()
      }, SAVE_DEBOUNCE_MS)
    })
  }

  const flushPendingSaves = (): void => {
    pendingSaves.current.forEach(({ timeout, save }) => {
      cancelTimeout(timeout)
      save()
    })
    pendingSaves.current.clear()
  }

  // Going away (the panel closed, or moved to another column, which mounts a panel of its own) sends what was still
  // waiting to be saved, to this panel's column: each save was made with its column's id and options, so it can't
  // land on the next column, and isn't lost either.
  useEffect(() => () => flushPendingSaves(), [])

  // The debounced option-list save reads the column's other kindOptions when it finally fires, not when
  // it was scheduled, so a toggle flipped mid-edit isn't written back stale.
  const kindOptionsRef = useRef(currentKindOptions)
  useEffect(() => {
    kindOptionsRef.current = currentKindOptions
  })

  // The option-list save on its way, and the list waiting to be sent after it (see sendManualOptions).
  const manualSaveInFlight = useRef(false)
  const queuedManualOptions = useRef<string[] | null>(null)

  useEffect(() => {
    const columnFound = isNew
      ? draftRef.current
      : spreadsheetData.viewDetails.columns.find((col) => col.publicId === menuState.columnId)
    // An answer for an option list while a newer one is still to be answered isn't the list on screen any more: the
    // last answer is.
    if (manualSaveInFlight.current || queuedManualOptions.current) return
    if (kind && SELECT_COLUMN_TYPES.includes(kind)) {
      setCurrentKindOptions(mergeKindOptions(columnFound?.kindOptions))
    } else {
      setCurrentKindOptions(DEFAULT_KIND_OPTIONS)
    }
  }, [
    kind,
    menuState.columnNumber !== undefined &&
      spreadsheetData.viewDetails.columns[menuState.columnNumber] &&
      spreadsheetData.viewDetails.columns[menuState.columnNumber].kindOptions
  ])

  // Everything held for one column has to be dropped when the panel moves to another: the drafts the
  // server hasn't confirmed, the drilled-into editor, and the select-source state loaded for it.
  useEffect(() => {
    setPendingConfigPatch({})
    setSubPanel(menuState.columnPanel ?? null)
    setSubPanelFullScreen(false)
    setSelectedSourceView(undefined)
    setDependencyColumn(NO_DEPENDENCY)
    setOptionsMode(column?.kindOptions?.tableOptions?.tableId ? 'table' : 'manual')
    setSourceDialogOpen(false)
    setOptionSearch('')
    setSourceMenu(INITIAL_CONTEXT_MENU_STATE)
    setDefaultShown(!!column?.defaultValue)
    setDefaultDraft(
      column && DATE_COLUMN_TYPES.includes(column.kind)
        ? formatDefaultDateForInput(column.defaultValue, column.kind)
        : (column?.defaultValue ?? '')
    )
  }, [menuState.columnId, menuState.columnPanel])

  // The source view as the user can read it, for the names in the panel's summary of it, whether or not the
  // source dialog (whose pickers load it too) is open.
  const sourceViewId = currentKindOptions.tableOptions.viewId
  // Whether it can be read is asked again of each new source, so picking a readable one clears the notice.
  // It's read again for each column the panel moves to, as that clears it, and a response for a source since
  // left behind is ignored. Access refused is shown in place; any other failure is reported.
  useEffect(() => {
    if (!sourceViewId) {
      setSourceRestricted(false)
      return
    }
    if (selectedSourceView?.publicId === sourceViewId) {
      setSourceRestricted(false)
      return
    }
    let stale = false
    api
      .getTableView(sourceViewId, { ignoreCachedOptions: true })
      .then((response) => {
        if (stale) return
        setSelectedSourceView(response.data)
        setSourceRestricted(false)
      })
      .catch((error) => {
        if (stale) return
        if (error?.status === 403) setSourceRestricted(true)
        else displayErrorMessage(error)
      })
    return () => {
      stale = true
    }
  }, [sourceViewId, menuState.columnId])

  // A new column has no options cached for it until it's created, so its preview reads the source column's values.
  const draftSourceColumnId = isNew ? currentKindOptions.tableOptions.columnId : ''
  useEffect(() => {
    // An existing column's options come back with its save (handleSaveSource), which says when they're loading.
    if (!isNew) return
    if (!sourceViewId || !draftSourceColumnId) {
      setSourceOptionsLoading(false)
      return
    }
    // Another source picked before these values arrive makes them stale: they're ignored.
    let stale = false
    setSourceOptionsLoading(true)
    api
      .getTableViewDistinct(sourceViewId, draftSourceColumnId)
      .then((response) => {
        if (stale) return
        setCurrentKindOptions((previous) => ({
          ...previous,
          tableOptions: { ...previous.tableOptions, cachedOptions: response.data }
        }))
      })
      .catch(() => undefined)
      .finally(() => {
        if (!stale) setSourceOptionsLoading(false)
      })
    return () => {
      stale = true
    }
  }, [sourceViewId, draftSourceColumnId])

  // The distinct values the table already holds in this column. Walking every row is too expensive to
  // repeat per render, so it is recomputed only when the column or its rows change; which of them are
  // still unused is a cheap set difference below. Blanks are skipped — an empty option is never useful,
  // and the bulk-add this replaced used to create one.
  const [columnValues, setColumnValues] = useState<string[]>([])
  useEffect(() => {
    if (!column || isNew || !SELECT_COLUMN_TYPES.includes(column.kind)) {
      setColumnValues([])
      return
    }
    const distinct = new Set<string>()
    // With bitmap model, all rows (visible + filtered) are in spreadsheetData.rows
    getFilterValues(
      spreadsheetData.rows,
      column,
      column.publicId,
      spreadsheetData.columnIdToIndex,
      spreadsheetData.columnStore
    ).forEach((value: unknown) => {
      const text = value === null || value === undefined ? '' : String(value)
      if (text !== '') distinct.add(text)
    })
    setColumnValues([...distinct].sort((valueA, valueB) => valueA.localeCompare(valueB)))
  }, [menuState.columnId, column?.kind, spreadsheetData.rows])

  const handleUpdateColumnSuccess = () => {
    if (!isNew) setSnackbarMessage({ status: 'success', message: 'The column was updated successfully!' })
    setProcessing(false)
  }

  // Every change the panel makes to the column: saved to it, or, for a column not created yet, kept on its draft.
  const saveColumn = (
    field: Parameters<typeof updateColumn>[1],
    value: Parameters<typeof updateColumn>[5],
    onSuccess: () => void,
    onError: (error: any) => void
  ): void => {
    if (isNew) {
      const next = { ...draftRef.current, [field]: value } as ITableViewColumn
      draftRef.current = next
      setDraftColumn(next)
      onSuccess()
      return
    }
    if (!publicId) return
    updateColumn(project.publicId, field, spreadsheetData, setSpreadsheetData, publicId, value, onSuccess, onError)
  }

  const handleUpdateColumnError = (error: any) => {
    setProcessing(false)
    setSelectedKind(undefined)
    displayErrorMessage(error)
  }

  // The name is the one field not saved on a debounce while typing: it commits on Enter, blur, or
  // panel close. See useColumnRename for the serialized save behaviour (issue #10385).
  const rename = useColumnRename({
    savedName: column?.name ?? '',
    save: (newName) => {
      // A new column's name only goes on its draft, so there's nothing for other changes to wait for.
      if (!isNew) setProcessing(true)
      return new Promise<void>((resolve, reject) => {
        if (!publicId) {
          reject(new Error('Column cannot be renamed'))
          return
        }
        saveColumn(
          'name',
          newName,
          () => resolve(),
          (error) => reject(error)
        )
      }).finally(() => setProcessing(false))
    },
    onSaved: handleUpdateColumnSuccess,
    getErrorMessage: (error) => getApiErrorMessage(error, 'The column name could not be saved.')
  })

  // A new column starts on its name, selected, to type over the one it was given.
  const nameInputRef = useRef<HTMLInputElement>(null)
  useEffect(() => {
    if (!isNew || !menuState.open) return
    nameInputRef.current?.focus()
    nameInputRef.current?.select()
  }, [isNew, menuState.open])

  // Show the column's saved name whenever the panel opens or switches column. The panel's state
  // outlives the menu (Menu renders null when closed), so without this a draft would leak across.
  useEffect(() => {
    if (!menuState.open) return
    rename.reset(column?.name ?? '')
  }, [menuState.open, menuState.columnId])

  // Send one validation/formatting field to the API. The draft keeps showing `value` until the response
  // comes back, at which point the saved column is authoritative again — unless the user has since
  // changed the same field, in which case their newer draft stays.
  const saveConfigField = (key: ColumnConfigKey, value: ColumnConfigValue[ColumnConfigKey]): void => {
    if (!publicId) return
    const settle = (): void =>
      setPendingConfigPatch((pending) => {
        if (pending[key] !== value) return pending
        return Object.fromEntries(Object.entries(pending).filter(([field]) => field !== key))
      })
    setProcessing(true)
    saveColumn(
      key,
      value,
      () => {
        settle()
        handleUpdateColumnSuccess()
      },
      (error) => {
        settle()
        handleUpdateColumnError(error)
      }
    )
  }

  // Route a patch from the shared validation/formatting components to the column. Each component emits
  // one key at a time; the key is already an `updateColumn` field, so it is sent as it is.
  const handleConfigChange = (patch: Partial<ColumnConfigValue>): void => {
    setPendingConfigPatch((pending) => ({ ...pending, ...patch }))
    // A text pattern that won't compile never gets here: its field won't save it.
    ;(Object.keys(patch) as ColumnConfigKey[]).forEach((key) => {
      saveConfigField(key, patch[key] as ColumnConfigValue[ColumnConfigKey])
    })
  }

  const handleScriptEnabledChange = (checked: boolean) => {
    if (isJoined || !publicId || checked === undefined) return
    saveColumn('scriptEnabled', checked, handleUpdateColumnSuccess, handleUpdateColumnError)
  }

  const handleDescriptionChange = (description: EditorContent) => {
    if (!publicId) return
    debounceSave('description', () => {
      setProcessing(true)
      saveColumn(
        'description',
        isEditorEmpty(description) ? null : description,
        handleUpdateColumnSuccess,
        handleUpdateColumnError
      )
    })
  }

  const handleDefaultValueChange = (value: string | null) => {
    if (!publicId) return
    debounceSave('defaultValue', () => {
      setProcessing(true)
      saveColumn('defaultValue', value || null, handleUpdateColumnSuccess, handleUpdateColumnError)
    })
  }

  const parseMultiselectDefault = (value: string | null | undefined): string[] => {
    if (!value) return []
    let parsed: unknown
    try {
      parsed = JSON.parse(value)
    } catch {
      return []
    }
    return Array.isArray(parsed) ? parsed : []
  }

  const [selectDefault, setSelectDefault] = useState<string | null>(column?.defaultValue ?? null)

  const [multiselectDefaults, setMultiselectDefaults] = useState<string[]>(
    parseMultiselectDefault(column?.defaultValue)
  )

  const handleSelectDefaultValueChange = (option: string) => {
    const newValue = option === selectDefault ? null : option
    setSelectDefault(newValue)
    handleDefaultValueChange(newValue)
  }

  const handleMultiselectDefaultValueChange = (option: string) => {
    setMultiselectDefaults((prev) => {
      const exists = prev.includes(option)
      const updated = exists ? prev.filter((v) => v !== option) : [...prev, option]
      const serialized = updated.length > 0 ? JSON.stringify(updated) : null
      handleDefaultValueChange(serialized)
      return updated
    })
  }

  const [checkboxDefault, setCheckboxDefault] = useState<string | null>(
    column?.defaultValue === 'true' || column?.defaultValue === 'false' ? column.defaultValue : null
  )

  const handleCheckboxDefaultValueChange = (option: string) => {
    const newValue = option === checkboxDefault ? null : option
    setCheckboxDefault(newValue)
    handleDefaultValueChange(newValue)
  }

  // Flag a stored default that no longer matches the column type (e.g. a "N/A" text
  // default left behind after switching the column to date). Such a default is ignored
  // by the backend on insert; this shows the user why. Type-specific inputs below stop
  // new mismatches from being entered, so this mainly catches stale values.
  const defaultValueError = kind ? getDefaultValueError(kind, column?.defaultValue) : null

  const handleOnKindChange = (selectedKind: string) => {
    const newKind = selectedKind as IColumnTypes
    // A column not created yet has no values to convert, so its type changes without asking.
    if (isNew) {
      saveColumn('kind', newKind, handleUpdateColumnSuccess, handleUpdateColumnError)
      return
    }
    const isGroupedColumn = !!spreadsheetData.userConfiguration.groupSettings.find(
      (group) => group.columnId === publicId
    )

    if (isGroupedColumn && BLOCK_GROUP_BY.includes(newKind)) {
      setSnackbarMessage({
        status: 'error',
        message: `This column groups the view, and ${COLUMN_TYPE_NAMES[newKind]} columns can't be used for grouping. Remove it from the grouping first.`
      })
      return
    }
    setSelectedKind(newKind)
    setChangeColumnKindModalState(true)
  }

  // Stop taking the options from a table: asked first, then the source is cleared and the list is typed in
  // again.
  const [confirmManualList, setConfirmManualList] = useState<boolean>(false)
  const switchToManualList = (): void => {
    if (tableId) setConfirmManualList(true)
    else setOptionsMode('manual')
  }

  // Choosing to source the options from a table opens the dialog that sets the source up.
  const openSourcePicker = (): void => {
    const {
      tableId: draftTable,
      viewId: draftView,
      columnId: draftColumn,
      liveValues
    } = currentKindOptions.tableOptions
    setSourceDraft({
      tableId: draftTable ?? '',
      viewId: draftView ?? '',
      columnId: draftColumn ?? '',
      liveValues: !!liveValues
    })
    setDraftSourceView(undefined)
    setSourcePickerOpen(true)
  }

  // Linking a table starts on its source, the one thing it can't do without.
  const openSourceDialog = (): void => {
    const linked = !!currentKindOptions.tableOptions.columnId
    setSourceDialogLinked(linked)
    setSourceDialogOpen(true)
    if (!linked) openSourcePicker()
  }

  const handleOptionsModeChange = (mode: SelectOptionsMode): void => {
    setOptionsMode(mode)
    if (mode === 'table' && !currentKindOptions.tableOptions.columnId) openSourceDialog()
  }

  // The source picked in its dialog, and whether rows follow it, saved at once: a new table or view takes the
  // column's dependencies and sourced options with it (they belonged to the old one).
  const handleSaveSource = (): void => {
    const { tableId: newTableId, viewId: newViewId, columnId: newColumnId, liveValues: newLiveValues } = sourceDraft
    const sourceColumn = draftSourceView?.columns.find((col) => col.publicId === newColumnId)
    if (!sourceColumn) return
    if (BLOCK_SELECT_OPTION_TYPES.includes(sourceColumn.kind)) {
      setSnackbarMessage({
        status: 'error',
        message: `${COLUMN_TYPE_NAMES[sourceColumn.kind]} columns can't supply select options. Choose a different column.`
      })
      return
    }
    const previous = currentKindOptions
    const tableOptions = previous.tableOptions
    const sameView = newTableId === tableOptions.tableId && newViewId === tableOptions.viewId
    const newCurrentKindOptions = settleAutoSelect(previous, {
      ...previous,
      // In the same view the dependencies stay, but not one that matches on the column the options now come from.
      tableOptions: sameView
        ? {
            ...tableOptions,
            columnId: newColumnId,
            liveValues: newLiveValues,
            dependencies: (tableOptions.dependencies ?? []).filter(
              (dependency) => dependency.columnJoinId !== newColumnId
            )
          }
        : {
            ...tableOptions,
            cachedOptions: [],
            tableId: newTableId,
            viewId: newViewId,
            columnId: newColumnId,
            dependencies: [],
            liveValues: newLiveValues
          }
    })

    setProcessing(true)
    setCurrentKindOptions(newCurrentKindOptions)
    if (draftSourceView) setSelectedSourceView(draftSourceView)
    setDependencyColumn(NO_DEPENDENCY)
    setSourcePickerOpen(false)
    // An existing column comes back from the save with the new source's options. A new column's are read by the
    // effect above once its source column changes, which ends the loading; until then it's loading from here, so the
    // panel never shows the empty list in between.
    const readsNewValues = !isNew || newViewId !== tableOptions.viewId || newColumnId !== tableOptions.columnId
    if (readsNewValues) setSourceOptionsLoading(true)
    handleUpdateKindOptions(
      newCurrentKindOptions,
      (error) => {
        setSourceOptionsLoading(false)
        setCurrentKindOptions(previous)
        handleUpdateColumnError(error)
      },
      () => {
        if (!isNew) setSourceOptionsLoading(false)
      }
    )
  }

  const handleRemoveTableOptions = () => {
    if (currentKindOptions.tableOptions.tableId) {
      const newCurrentKindOptions = settleAutoSelect(currentKindOptions, {
        ...currentKindOptions,
        // The source's options go with it; the hand-entered ones stay, so rows keep their values.
        tableOptions: {
          ...currentKindOptions.tableOptions,
          tableId: '',
          viewId: '',
          columnId: '',
          dependencies: [],
          cachedOptions: []
        }
      })
      setCurrentKindOptions(newCurrentKindOptions)
      handleUpdateKindOptions(newCurrentKindOptions, handleUpdateColumnError)
    }
  }

  // Whether a column of this table needs a column of the source named to match it by: any but a select linked
  // to the very same source view, which matches by its own source column.
  const needsDependencyJoin = (ownColumn?: ITableViewColumn): boolean =>
    !ownColumn ||
    !SELECT_COLUMN_TYPES.includes(ownColumn.kind) ||
    ownColumn.kindOptions?.tableOptions?.tableId !== tableId ||
    ownColumn.kindOptions?.tableOptions?.viewId !== viewId

  const saveDependencies = (newDependencies: SelectDependencyColumn[], failure: string): void => {
    const newCurrentKindOptions = settleAutoSelect(currentKindOptions, {
      ...currentKindOptions,
      tableOptions: { ...currentKindOptions.tableOptions, dependencies: newDependencies }
    })
    setCurrentKindOptions(newCurrentKindOptions)
    handleUpdateKindOptions(newCurrentKindOptions, (error) => handleUpdateColumnError(error ?? failure))
  }

  // Whether a column of this table is of a kind that can't be a dependency, said so when it is.
  const cannotBeDependency = (ownColumn?: ITableViewColumn): boolean => {
    if (!ownColumn || !BLOCK_SELECT_OPTION_TYPES.includes(ownColumn.kind)) return false
    setSnackbarMessage({
      status: 'error',
      message: `${COLUMN_TYPE_NAMES[ownColumn.kind]} columns can't be used as dependencies. Choose a different column.`
    })
    return true
  }

  // A column picked from "+ Add dependency": added at once when it matches by its own source, otherwise held
  // as a rule waiting for the source column it matches.
  const handleStartDependency = (columnId: string): void => {
    const ownColumn = spreadsheetData.viewDetails.columns.find((col) => col.publicId === columnId)
    if (cannotBeDependency(ownColumn)) return
    if (needsDependencyJoin(ownColumn)) {
      setDependencyColumn({ columnId, columnJoinId: '' })
      return
    }
    saveDependencies([...dependencies, { columnId, columnJoinId: '' }], "The dependency couldn't be added. Try again.")
  }

  // This table's column a dependency is on, changed on a new one (index -1) or an existing one. A column that
  // matches by a source column of its own needs none named; one that doesn't keeps the one named, or, with
  // none yet, waits as a new rule for it.
  const handleDependencyColumn = (index: number, columnId: string): void => {
    const ownColumn = spreadsheetData.viewDetails.columns.find((col) => col.publicId === columnId)
    if (cannotBeDependency(ownColumn)) return
    const current = index < 0 ? dependencyColumn : dependencies[index]
    const columnJoinId = needsDependencyJoin(ownColumn) ? current.columnJoinId : ''
    if (index < 0) {
      if (!needsDependencyJoin(ownColumn) || columnJoinId) {
        saveDependencies([...dependencies, { columnId, columnJoinId }], "The dependency couldn't be added. Try again.")
        setDependencyColumn(NO_DEPENDENCY)
      } else setDependencyColumn({ columnId, columnJoinId: '' })
      return
    }
    if (needsDependencyJoin(ownColumn) && !columnJoinId) {
      saveDependencies(
        dependencies.filter((_, position) => position !== index),
        "The dependency couldn't be changed. Try again."
      )
      setDependencyColumn({ columnId, columnJoinId: '' })
      return
    }
    saveDependencies(
      dependencies.map((dependency, position) => (position === index ? { columnId, columnJoinId } : dependency)),
      "The dependency couldn't be changed. Try again."
    )
  }

  // The source column a dependency matches, picked for a new one (index -1) or changed on an existing one.
  const handleDependencyJoin = (index: number, columnJoinId: string): void => {
    if (columnJoinId === currentKindOptions.tableOptions.columnId) {
      setSnackbarMessage({
        status: 'error',
        message:
          "This column already supplies the options, so it can't also be a dependency. Choose a different column."
      })
      return
    }
    if (index < 0) {
      saveDependencies(
        [...dependencies, { columnId: dependencyColumn.columnId, columnJoinId }],
        "The dependency couldn't be added. Try again."
      )
      setDependencyColumn(NO_DEPENDENCY)
      return
    }
    saveDependencies(
      dependencies.map((dependency, position) => (position === index ? { ...dependency, columnJoinId } : dependency)),
      "The dependency couldn't be changed. Try again."
    )
  }

  const handleRemoveTableDependency = (index: number) => {
    const newDependencies = [...currentKindOptions.tableOptions.dependencies]
    newDependencies.splice(index, 1)
    const newCurrentKindOptions = settleAutoSelect(currentKindOptions, {
      ...currentKindOptions,
      tableOptions: {
        ...currentKindOptions.tableOptions,
        dependencies: newDependencies
      }
    })
    setCurrentKindOptions(newCurrentKindOptions)
    handleUpdateKindOptions(newCurrentKindOptions, handleUpdateColumnError)
  }

  // Shown at once, as the other kind options are: a new column has no server answer to pick the change up from, and
  // creates itself with what's shown.
  const handleSetAutopopulateValues = (autopopulate: boolean) => {
    if (processing) return
    const next = { ...currentKindOptions, autopopulate }
    setCurrentKindOptions(next)
    handleUpdateKindOptions(next, handleUpdateColumnError)
  }

  const handleSetExpandOptionsViaCellEntry = (expandOptionsViaCellEntry: boolean) => {
    if (processing) return
    const next = { ...currentKindOptions, expandOptionsViaCellEntry }
    setCurrentKindOptions(next)
    handleUpdateKindOptions(next, handleUpdateColumnError)
  }

  // Apply a new manual list: shown at once, then persisted. A typed edit waits out the same pause the
  // other typed fields do so a save isn't sent per keystroke; adding, deleting, reordering and sorting
  // are single deliberate actions, so they save immediately.
  const commitManualOptions = (nextOptions: string[], debounce = false): void => {
    setCurrentKindOptions((previous) => ({ ...previous, manualOptions: nextOptions }))

    const save = (): void => {
      // Inline editing makes a duplicate easy to type into existence; the list is only sent once it is
      // valid, and the draft stays on screen for the user to fix.
      if (new Set(nextOptions).size !== nextOptions.length) {
        setSnackbarMessage({
          status: 'error',
          message: 'Each option must be different. Change or remove the repeated option.'
        })
        return
      }
      sendManualOptions(nextOptions)
    }

    if (!debounce) {
      cancelPendingSave('manualOptions')
      save()
      return
    }
    debounceSave('manualOptions', save)
  }

  // One option-list save at a time: a list saved while another is on its way waits for that one's answer, and only
  // the latest list waiting is sent, so the server applies them in order and an older answer can't put back an
  // option since removed.
  const sendManualOptions = (options: string[]): void => {
    if (manualSaveInFlight.current) {
      queuedManualOptions.current = options
      return
    }
    manualSaveInFlight.current = true
    const settle = (): void => {
      manualSaveInFlight.current = false
      const next = queuedManualOptions.current
      queuedManualOptions.current = null
      if (next) sendManualOptions(next)
    }
    handleUpdateKindOptions(
      { ...kindOptionsRef.current, manualOptions: options },
      (error) => {
        handleUpdateColumnError(error)
        settle()
      },
      settle
    )
  }

  const handleAddManualOption = (value: string): void => {
    const trimmed = value.trim()
    if (!trimmed) return
    if (getOptions().includes(trimmed)) {
      setSnackbarMessage({ status: 'error', message: `'${trimmed}' is already an option.` })
      return
    }
    commitManualOptions([...manualOptions, trimmed])
  }

  const handleUpdateManualOption = (index: number, value: string): void =>
    commitManualOptions(
      manualOptions.map((option, position) => (position === index ? value : option)),
      true
    )

  const handleRemoveManualOption = (index: number): void =>
    commitManualOptions(manualOptions.filter((_, position) => position !== index))

  const handleMoveManualOption = (from: number, to: number): void => {
    if (from === to || from < 0 || to < 0 || from >= manualOptions.length || to >= manualOptions.length) return
    const next = [...manualOptions]
    const [moved] = next.splice(from, 1)
    next.splice(to, 0, moved)
    commitManualOptions(next)
  }

  // Sorts A→Z, or Z→A when the list is already ascending: the toggle in the Options heading and the Options page.
  const handleSortManualOptions = (): void => {
    const ascending = [...manualOptions].sort((a, b) => a.localeCompare(b))
    commitManualOptions(isAscending(manualOptions) ? [...ascending].reverse() : ascending)
  }
  const sortButton = (className: string) => {
    const label = isAscending(manualOptions) ? 'Sort Z to A' : 'Sort A to Z'
    return (
      <Tooltip content={label}>
        <Button
          internalType="transparent"
          aria-label={label}
          className={`rounded-md p-0 text-muted hover:bg-surface-hover hover:text-body ${className}`}
          onClick={handleSortManualOptions}
        >
          {isAscending(manualOptions) ? <SortZA /> : <SortAZ />}
        </Button>
      </Tooltip>
    )
  }

  const sourcedOptions = sourcedOptionsOf(currentKindOptions.tableOptions.cachedOptions)

  const handleUpdateKindOptions = (
    kindOptions: SelectKindOptions,
    onError: (error?: string) => void,
    onSuccess?: () => void
  ) => {
    setProcessing(true)
    const newKindOptions = kindOptionsToSave(kindOptions)
    if (publicId) {
      saveColumn(
        'kindOptions',
        newKindOptions,
        () => {
          handleUpdateColumnSuccess()
          onSuccess?.()
        },
        onError
      )
    }
  }

  // Every option the column offers: its own, then the source's, each once.
  const getOptions = (): string[] => [
    ...new Set([...currentKindOptions.manualOptions, ...sourcedOptions.map((option) => option.value)])
  ]

  // Picking several values in a row debounces into one save, the same as a typed edit, so a burst of
  // clicks doesn't become a burst of requests.
  const handleAddColumnValues = (values: string[]): void => {
    const additions = values.filter((value) => !manualOptions.includes(value))
    if (additions.length === 0) return
    commitManualOptions([...manualOptions, ...additions], true)
  }

  const manualOptions = currentKindOptions.manualOptions
  const takenOptions = new Set(getOptions())
  const unusedColumnValues = columnValues.filter((value) => !takenOptions.has(value))
  // The columns of this table the options depend on, in the order their values make up a group's key.
  const dependsOnColumns = (currentKindOptions.tableOptions.dependencies ?? []).map((dependency) =>
    spreadsheetData.viewDetails.columns.find((col) => col.publicId === dependency.columnId)
  )
  const dependsOnNameList = dependsOnColumns.map((col) => col?.name ?? 'a column')
  // "Country", "Country and Region", "Country, Region and Segment".
  const dependsOnNames =
    dependsOnNameList.length > 1
      ? `${dependsOnNameList.slice(0, -1).join(', ')} and ${dependsOnNameList[dependsOnNameList.length - 1]}`
      : (dependsOnNameList[0] ?? '')
  const tableId = currentKindOptions.tableOptions.tableId
  const viewId = currentKindOptions.tableOptions.viewId
  const viewColumnId = currentKindOptions.tableOptions.columnId
  const dependencies = currentKindOptions.tableOptions.dependencies || []
  // This table's columns that a new dependency can be on: not this one, not one already used, and of a kind
  // that can match; and the source's columns it can match, any but the one the options come from.
  // `keep`: the column a rule already has, offered back to it when it's changed.
  const ownColumnOptions = (keep?: string) =>
    spreadsheetData.viewDetails.columns
      .filter(
        (col) =>
          col.publicId === keep ||
          (col.publicId !== column?.publicId &&
            !BLOCK_SELECT_OPTION_TYPES.includes(col.kind) &&
            !dependencies.some((dependency) => dependency.columnId === col.publicId))
      )
      .map((col) => ({ label: col.name, value: col.publicId, icon: <IconSelector kind={col.kind} /> }))
  const dependencyColumnOptions = ownColumnOptions()
  // Names come from the source view as the user can read it, which also covers a table they can only
  // reach through a view-level role. A stale view from an earlier selection is ignored.
  const sourceView = selectedSourceView?.publicId === viewId ? selectedSourceView : undefined
  const selectedTableViewColumns = sourceView?.columns
  const dependencyJoinOptions = (selectedTableViewColumns ?? [])
    .filter((col) => col.publicId !== viewColumnId && !BLOCK_SELECT_OPTION_TYPES.includes(col.kind))
    .map((col) => ({ label: col.name, value: col.publicId, icon: <IconSelector kind={col.kind} /> }))
  const selectedTableName = sourceView?.documentTable?.name
  const selectedViewName = sourceView?.name
  const disabled = isJoined || processing
  const isSynced = column?.isSynced
  const disableSelectConfiguration = isJoined || isSynced
  const isReadOnly = isJoined || isSynced
  const scriptsAllowed = !isReadOnly && scriptEnabled
  const isSelectColumn =
    (selectedKind && SELECT_COLUMN_TYPES.includes(selectedKind)) || (kind && SELECT_COLUMN_TYPES.includes(kind))
  const showSelectOptions = !disableSelectConfiguration && isSelectColumn

  // Leaving the formula editor with unsaved text is the one navigation worth interrupting; the guard is
  // the same one Menu applies to closing the whole panel.
  const closeSubPanel = (): void => {
    if (formulaHasChanges && !window.confirm('You have an unsaved formula. Are you sure you want to discard it?'))
      return
    setFormulaHasChanges(false)
    setSubPanel(null)
    setSubPanelFullScreen(false)
    // The Options page's search is for that visit; the next starts on the whole list.
    setOptionSearch('')
    setMissingShown(false)
  }

  const handleClose = (): void => {
    if (newColumn) {
      // While it's being created it can't be called off: the panel closes once the column is made.
      if (newColumn.creating) return
      newColumn.onCancel()
      return
    }
    flushPendingSaves()
    rename.commit(rename.draftName, { onError: handleUpdateColumnError })
  }

  const handleCreate = (): void => {
    if (!newColumn || !draftRef.current) return
    flushPendingSaves()
    const name = rename.draftName.trim() || draftRef.current.name
    newColumn.onCreate({ ...draftRef.current, name, kindOptions: { ...currentKindOptions } })
  }

  const baseWidth = width ?? EDIT_COLUMN_MENU_WIDTH

  // The hand-entered options: the first five on the panel's page, under View all, and the whole list on the
  // Options page.
  const manualOptionsList = (preview: boolean) => (
    <ColumnManualOptions
      options={manualOptions}
      onAdd={handleAddManualOption}
      onUpdate={handleUpdateManualOption}
      onRemove={handleRemoveManualOption}
      onMove={handleMoveManualOption}
      suggestions={unusedColumnValues}
      onAddMany={handleAddColumnValues}
      expandOptionsViaCellEntry={currentKindOptions.expandOptionsViaCellEntry}
      onExpandOptionsChange={viewColumnId || !preview ? undefined : handleSetExpandOptionsViaCellEntry}
      previewLimit={preview ? PREVIEWED_OPTIONS : undefined}
      onViewAll={preview ? () => setSubPanel('options') : undefined}
      filter={preview ? undefined : optionSearch}
    />
  )
  const manualMatchCount = optionSearch
    ? manualOptions.filter((option) => optionMatches(option, optionSearch)).length
    : manualOptions.length
  const manualOptionsPane = manualOptionsList(true)

  // Options taken from a column in another table, set up in a dialog of their own: the source pickers, the
  // switches that govern how the values are kept in step, the dependency columns that narrow them, and a
  // look at the options themselves — searchable, and never all of a large source at once.
  const sourceColumnLabel = selectedTableViewColumns?.find((col) => col.publicId === viewColumnId)?.name
  const sourceLabel =
    selectedTableName && selectedViewName && sourceColumnLabel
      ? `${selectedTableName} › ${selectedViewName} › ${sourceColumnLabel}`
      : undefined
  // The source as a path, "Plans › Default › ⊙ Plan name": its view and table by name, its column with its type
  // icon, the chevrons between them a shade lighter than the names.
  const sourceColumnKind = selectedTableViewColumns?.find((col) => col.publicId === viewColumnId)?.kind
  const sourcePathInline = (chevronClass: string, tableClass?: string): React.ReactNode => (
    <>
      <span className={tableClass}>{selectedTableName}</span>
      <span className={`mx-1 ${chevronClass}`}>›</span>
      {selectedViewName}
      <span className={`mx-1 ${chevronClass}`}>›</span>
      <span className="whitespace-nowrap">
        {sourceColumnKind && (
          <span className="mr-1 inline-flex align-[-2px] [&_svg]:size-3.5">
            <IconSelector kind={sourceColumnKind} />
          </span>
        )}
        {sourceColumnLabel}
      </span>
    </>
  )
  // In a sentence, with the table's icon before it: "⊞ Plans › Default › ⊙ Plan name".
  const sourcePath: React.ReactNode = sourceLabel ? (
    <span className="font-medium text-body">
      <span className="mr-1 inline-flex align-[-2px] [&_svg]:size-3.5">
        <Grid />
      </span>
      {sourcePathInline('text-faint')}
    </span>
  ) : (
    'The source column'
  )
  const sourcedMatches = optionSearch
    ? sourcedOptions.filter((option) => optionMatches(option.value, optionSearch))
    : sourcedOptions
  // A linked column's own hand-entered options the source doesn't have, kept from before the table was linked:
  // offered beside the table's in every row, whatever the dependency. Each with its place in the manual list, to
  // remove it by.
  const sourcedValues = sourceValuesOf(currentKindOptions.tableOptions.cachedOptions)
  const notInSource =
    optionsMode === 'table'
      ? manualOptions.map((value, index) => ({ value, index })).filter(({ value }) => !sourcedValues.has(value))
      : []
  const notInSourceMatches = optionSearch
    ? notInSource.filter((option) => optionMatches(option.value, optionSearch))
    : notInSource
  const linkedOptionCount = sourcedOptions.length + notInSource.length

  // The default a new row takes, as a field of the column's own kind: options to pick from, ticked or
  // not, a date, a number or text. Shown while values are typed in; a formula or script fills the cells.
  const showDefaultValue =
    !isJoined &&
    !scriptEnabled &&
    !formulaEnabled &&
    !isSynced &&
    !!kind &&
    !DEFAULT_VALUE_UNSUPPORTED_KINDS.includes(kind)
  const defaultValueControl = (): React.ReactNode => {
    if (!kind) return null
    if (kind === 'select' || kind === 'multiselect') {
      const opts = getOptions()
      const picked = kind === 'select' ? (selectDefault ? [selectDefault] : []) : multiselectDefaults
      const extra = picked.filter((value) => !opts.includes(value))
      return (
        <Select
          inputId="column-default-value"
          options={[...opts, ...extra].map((opt) => ({ label: opt, value: opt }))}
          optionsSelected={picked}
          multiselect={kind === 'multiselect'}
          onOptionClick={(option) =>
            kind === 'select' ? handleSelectDefaultValueChange(option) : handleMultiselectDefaultValueChange(option)
          }
          clearable={kind === 'select'}
          placeholder="No default"
          size="sm"
        />
      )
    }
    if (kind === 'checkbox') {
      return (
        <Select
          inputId="column-default-value"
          options={[
            { label: 'Ticked', value: 'true' },
            { label: 'Not ticked', value: 'false' }
          ]}
          optionsSelected={checkboxDefault ? [checkboxDefault] : []}
          onOptionClick={(option) => handleCheckboxDefaultValueChange(option)}
          clearable
          placeholder="No default"
          searchable={false}
          size="sm"
        />
      )
    }
    const isDate = DATE_COLUMN_TYPES.includes(kind)
    return (
      <Input
        id="column-default-value"
        className="h-8 rounded-md"
        type={defaultValueInputType(kind)}
        placeholder={kind === 'json' ? '{ "key": "value" }' : 'No default'}
        value={defaultDraft}
        onChange={(event) => {
          setDefaultDraft(event.target.value)
          handleDefaultValueChange((isDate ? toUTCString(event.target.value, kind) : event.target.value) || null)
        }}
      />
    )
  }

  // One of a group's conditions, the column and its value standing out from the words between: "Country is
  // France", after "When" for the first and "and" for the rest.
  const conditionOf = (value: string, index: number): React.ReactNode => (
    <>
      <span className="text-muted">{index === 0 ? 'When ' : 'and '}</span>
      <ColumnName column={dependsOnColumns[index]} />
      <span className="text-muted"> is </span>
      <span className="font-medium">{value}</span>
    </>
  )

  // A group's conditions in a sentence: "When Country is France and Region is EMEA".
  const conditionsOf = (group: string): React.ReactNode =>
    group.split(GROUP_KEY_SPLIT).map((value, index) => (
      <React.Fragment key={index}>
        {index > 0 && ' '}
        {conditionOf(value, index)}
      </React.Fragment>
    ))

  // A linked column's own options that the source doesn't have, each to remove with its ×, as a manual option is.
  const notInSourceList = (options: Array<{ value: string; index: number }>): React.ReactNode =>
    options.length > 0 && (
      <div className="flex flex-col gap-1" data-testid="options-not-in-source">
        {options.map(({ value, index }) => (
          <div
            key={`${index}-${value}`}
            className="flex h-8 items-center gap-2 rounded-md border border-divider bg-field pr-1 pl-3 text-[13px]"
          >
            <span className="min-w-0 flex-1 truncate">{value}</span>
            <Button
              internalType="transparent"
              aria-label={`Remove ${value}`}
              className="size-6 shrink-0 rounded p-0 text-muted hover:bg-red/10 hover:text-red [&_svg]:size-3.5"
              onClick={() => handleRemoveManualOption(index)}
            >
              <Cross />
            </Button>
          </div>
        ))}
      </div>
    )

  // Where a dependency is set, the source dialog: a pencil, shown while pointing at what it follows.
  const editDependencyButton = (shownOnHover: string): React.ReactNode => (
    <Tooltip content="Edit dependency">
      <Button
        internalType="transparent"
        aria-label="Edit dependency"
        className={`size-5 shrink-0 rounded p-0 text-muted opacity-0 transition-opacity ${shownOnHover} hover:bg-surface-hover hover:text-body focus-visible:opacity-100 [@media(hover:none)]:opacity-100 [&_svg]:size-3`}
        onClick={openSourceDialog}
      >
        <Pencil />
      </Button>
    </Tooltip>
  )

  // A group's values alone, as its heading gives them: "When France and EMEA", "When A, B and C". The card
  // over it has the conditions in full.
  const valuesOf = (group: string): React.ReactNode => {
    const values = group.split(GROUP_KEY_SPLIT)
    return (
      <>
        <span className="text-muted">When </span>
        {values.map((value, index) => (
          <React.Fragment key={index}>
            {index > 0 && <span className="text-muted">{index === values.length - 1 ? ' and ' : ', '}</span>}
            <span className="font-medium">{value}</span>
          </React.Fragment>
        ))}
      </>
    )
  }

  // A list of options as the panel shows them. With dependency columns they're grouped under the value of
  // this table's dependency column they're offered for: a small heading with that column's icon, then its
  // options indented along a guide line. Every option carries the blue table icon of the source it's from.
  // In the source dialog, where the dependency is set, the headings have no pencil to open it.
  const optionGroupsList = (groups: Array<{ group?: string; options: string[] }>, editable = true): React.ReactNode =>
    groups.map(({ group, options: groupOptions }, groupIndex) => {
      const rows = groupOptions.map((option, index) => (
        <div
          key={`${index}-${option}`}
          className="flex h-8 items-center gap-2 rounded-md border border-divider bg-field pr-2 pl-3 text-[13px]"
        >
          <span className="min-w-0 flex-1 truncate">{option}</span>
          <Tooltip content={`From ${sourceLabel ?? 'the source table'}`}>
            <span className="flex shrink-0 text-blue [&_svg]:size-3.5">
              <Grid />
            </span>
          </Tooltip>
        </div>
      ))
      if (group === undefined) return <React.Fragment key={`all-${groupIndex}`}>{rows}</React.Fragment>
      return (
        <div key={`${groupIndex}-${group}`} className="relative flex flex-col gap-1">
          {/* The values these options are for, on one line: "When France and EMEA". Pointing at it opens a card
              with the conditions in full, each column with its type icon: "When Country is France and Region is
              EMEA", and what that means. */}
          <div className="group/dependency flex h-7 min-w-0 items-center gap-1.5 text-[13px] text-body">
            <HoverCard
              plain
              className="flex min-w-0 items-center gap-1.5"
              title={<span className="font-normal">{conditionsOf(group)}</span>}
              content={`Each row is offered only the ${column?.name ?? 'options'} options for its own ${dependsOnNames}.`}
            >
              {/* In blue, as the source's table icon is: these options depend on other columns, as it says. */}
              <span className="flex shrink-0 text-blue [&_svg]:size-3.5">
                <Dependency />
              </span>
              <span className="min-w-0 truncate">{valuesOf(group)}</span>
            </HoverCard>
            {/* Where the dependency is set: a pencil just after the values, shown while pointing at them. */}
            {editable && editDependencyButton('group-hover/dependency:opacity-100')}
          </div>
          {/* The guide line runs down from under the icon, past the options it heads. */}
          <span aria-hidden className="absolute top-[25px] bottom-0 left-[6px] w-px bg-divider" />
          <div className="ml-[7px] flex flex-col gap-1 pl-[13px]">{rows}</div>
        </div>
      )
    })

  // A linked column's whole list, as the Options page and the source dialog show it: the source's options,
  // under a banner saying how many of the column's own values the source doesn't have, which opens a list of
  // them to remove.
  const showingMissing = missingShown && notInSource.length > 0
  const shownMatchCount = showingMissing ? notInSourceMatches.length : sourcedMatches.length
  const shownCount = showingMissing ? notInSource.length : sourcedOptions.length
  // `collapsed`: behind a search icon until it's pressed, and white and bordered on the preview's grey.
  const linkedFilterRow = (disabled = false, collapsed = false): React.ReactNode =>
    ((!showingMissing && !collapsed) || searchOpen) && (
      // 8px above and below, between the lines over and under it, lined up with the options' edges.
      <div
        className={`shrink-0 border-b border-divider px-2 pt-1 pb-2 ${collapsed ? '[&_label]:border [&_label]:border-divider [&_label]:bg-surface-raised' : ''}`}
      >
        <MenuSearch
          ref={showingMissing || collapsed ? collapsedSearchRef : undefined}
          aria-label={showingMissing ? 'Search the missing options' : 'Search the options'}
          placeholder="Search options"
          value={optionSearch}
          onChange={(event) => setOptionSearch(event.target.value)}
          disabled={disabled}
        />
      </div>
    )
  // What the column's own options the source doesn't have mean for its rows, said where they're counted.
  const missingTitle = `${optionCount(notInSource.length)} missing from source table`
  const missingSentence = (
    <>
      Some rows in the{' '}
      <span className="whitespace-nowrap">
        {kind && (
          <span className="mr-1 inline-flex align-[-2px] [&_svg]:size-3.5">
            <IconSelector kind={kind} />
          </span>
        )}
        <span className="font-medium text-body">{column?.name ?? 'this'}</span>
      </span>{' '}
      column do not match a value in the linked source table.
    </>
  )
  // Those options, said quietly after a count: a small warning and how many, the rest on hover, opening them.
  const missingLink = (onOpen: () => void): React.ReactNode =>
    notInSource.length > 0 && (
      // Close to the count it follows, the dot a few pixels from each.
      <>
        <span aria-hidden className="text-faint">
          ·
        </span>
        <Button
          internalType="transparent"
          data-testid="options-missing-link"
          aria-label={missingTitle}
          className="-ml-1 h-7 gap-1.5 rounded-md px-1 text-[13px] font-normal text-muted hover:bg-surface-hover hover:text-body [&_svg]:size-3.5"
          onClick={onOpen}
        >
          <Warning />
          <HoverCard title={missingTitle} content={missingSentence}>
            {notInSource.length.toLocaleString()} missing
          </HoverCard>
        </Button>
      </>
    )
  const openMissing = (): void => {
    setOptionSearch('')
    setMissingShown(true)
  }
  const closeMissing = (): void => {
    setOptionSearch('')
    setMissingShown(false)
    setSearchOpen(false)
  }
  // The search for the missing options, shown and focused from its icon.
  const missingSearchButton = !searchOpen && (
    <Tooltip content="Search">
      <Button
        internalType="transparent"
        aria-label={showingMissing ? 'Search the missing options' : 'Search the options'}
        className="size-6 shrink-0 rounded-md p-0 text-muted hover:bg-surface-hover hover:text-body [&_svg]:size-3.5"
        onClick={() => setSearchOpen(true)}
      >
        <Search />
      </Button>
    </Tooltip>
  )
  const linkedOptionsList = (editable: boolean): React.ReactNode => {
    // On the Options page as in the dialog's preview: a source still loading isn't one with no options.
    if (sourceOptionsLoading) return <LoadingSourceOptions />
    if (showingMissing)
      return (
        <>
          <p className="pb-1 text-[12px] whitespace-normal text-muted">
            {sourcePath} does not contain values matching the following list. Removing them will mark them as invalid
            input but not remove them from your table.
          </p>
          {shownMatchCount === 0 ? noOptionsMatch : notInSourceList(notInSourceMatches)}
        </>
      )
    return (
      <>
        {shownMatchCount === 0 && optionSearch && noOptionsMatch}
        {shownMatchCount === 0 && !optionSearch && <NoSourceOptions />}
        {shownMatchCount > 0 &&
          optionGroupsList(groupedOptions(sourcedMatches.slice(0, LISTED_SOURCE_OPTIONS)), editable)}
        {sourcedMatches.length > LISTED_SOURCE_OPTIONS && (
          <span className="pt-1 text-center text-[12px] text-muted">
            The first {LISTED_SOURCE_OPTIONS} of {sourcedMatches.length.toLocaleString()} are listed. Search to find
            others.
          </span>
        )}
      </>
    )
  }
  // How many options there are, and, while searching, how many of them match.
  const countLabel = (matched: number, total: number): string =>
    optionSearch ? `${matched.toLocaleString()} of ${optionCount(total)}` : optionCount(total)
  const linkedCountLabel = countLabel(shownMatchCount, shownCount)

  // Where the source view opens, from the links to it (only shown once there's a source).
  const sourceViewPath = tableId && viewId ? generatePath(TABLE, { projectId: project.publicId, tableId, viewId }) : ''
  // At the foot of the options' list, on the panel and on the Options page: the source table, in a new tab.
  const sourceTableLink = (
    <Tooltip content={`Open ${selectedTableName ?? 'the source table'}`}>
      <Link
        to={sourceViewPath}
        target="_blank"
        rel="noopener noreferrer"
        className="ml-auto flex h-7 items-center gap-1.5 rounded-md px-2 text-[13px] font-medium text-muted hover:bg-surface-hover hover:text-body [&_svg]:size-3.5"
      >
        Source table
        <ExternalLink />
      </Link>
    </Tooltip>
  )

  // The same, on the Source column dialog's button for the column the options come from.
  const sourceColumnButtonLabel = (): React.ReactNode => {
    if (sourceRestricted) return 'A source you cannot open'
    if (!sourceLabel) return 'Choose a source column'
    return sourcePathInline('text-faint', 'text-blue')
  }

  // What the source button says: the source's path once it has loaded, or why there's none to show.
  const sourceButtonLabel = (): React.ReactNode => {
    if (sourceRestricted) return 'A source you cannot open'
    if (!viewColumnId) return 'Choose a source'
    if (!sourceLabel) return 'Loading source'
    return sourcePathInline('text-blue/50')
  }

  // What a linked table's options come from, in blue, with what can be done to it (Update source, Switch to
  // manual list): to the right of the Options heading, and of the Options page's title.
  const sourceButton = (
    <Button
      internalType="transparent"
      className="h-7 min-w-0 shrink gap-1.5 rounded-md px-1.5 text-[13px] font-medium text-blue hover:bg-really-light-blue hover:text-blue [&_svg]:size-3.5 [&_svg]:shrink-0"
      aria-haspopup="menu"
      aria-expanded={sourceMenu.open}
      onClick={(event) => {
        const rect = event.currentTarget.getBoundingClientRect()
        setSourceMenu({
          open: true,
          top: `${rect.bottom + 4}px`,
          left: `${rect.right - SOURCE_MENU_WIDTH}px`,
          right: 'auto',
          bottom: 'auto'
        })
      }}
    >
      <Grid />
      <span className="min-w-0 truncate">{sourceButtonLabel()}</span>
      <ChevronDown />
    </Button>
  )

  // The source, or, for a manual list, the way to link one: to the right of the Options heading, and of the
  // Options page's title.
  const optionsSourceControl =
    optionsMode === 'table' ? (
      sourceButton
    ) : (
      <PanelAction icon={<Grid />} onClick={() => handleOptionsModeChange('table')}>
        Link a table
      </PanelAction>
    )

  // A linked table with nothing chosen from it yet: what's missing, and the ways out.
  const missingSource = (className: string): React.ReactNode => (
    <Empty
      className={className}
      icon={<Grid />}
      title="Missing source column"
      description="Choose the table, view and column the options come from."
      data-testid="options-no-source"
    >
      <div className="flex items-center gap-2">
        <Button internalType="outline" className={`h-8 px-3 text-[13px] ${BLUE_BUTTON}`} onClick={openSourceDialog}>
          Choose a source
        </Button>
        <Button
          internalType="outline"
          className="h-8 px-3 text-[13px] font-medium"
          onClick={() => setOptionsMode('manual')}
        >
          Use a manual list
        </Button>
      </div>
    </Empty>
  )

  // The table a rule's first column is in, as its blue icon alone (its name on hover, and in the sentence over
  // the rules), and a chevron on to the column: "⊞ ›".
  const tableOwnerSegment = (name: string | undefined): React.ReactNode =>
    name && (
      <span className="flex shrink-0 items-center gap-1 pl-1.5 select-none [&_svg]:size-3.5 [&_svg]:shrink-0">
        <Tooltip content={name}>
          <span className="flex text-blue" aria-label={name}>
            <Grid />
          </span>
        </Tooltip>
        <span className="flex text-faint">
          <Triangle type="right" />
        </span>
      </span>
    )
  // The words between a rule's parts, quiet: "and", "matches".
  const ruleWord = (word: string): React.ReactNode => (
    <span className="flex shrink-0 items-center px-1.5 text-[13px] text-muted select-none">{word}</span>
  )

  // A dependency as a rule: this table's column, then the source's column it matches. A select linked to the
  // same source matches by its own source column, so that one's shown rather than picked.
  const dependencyRule = (dependency: SelectDependencyColumn, index: number): React.ReactNode => {
    const ownColumn = spreadsheetData.viewDetails.columns.find((col) => col.publicId === dependency.columnId)
    const impliedJoinId = needsDependencyJoin(ownColumn) ? undefined : ownColumn?.kindOptions?.tableOptions?.columnId
    const impliedJoin = impliedJoinId && selectedTableViewColumns?.find((col) => col.publicId === impliedJoinId)
    const draft = index < 0
    return (
      <PanelRule
        key={draft ? 'dependency-new' : `dependency-${index}`}
        id={draft ? 'dependency-new' : `dependency-${index}`}
        removeLabel={`Remove the dependency on ${ownColumn?.name ?? 'this column'}`}
        // eslint-disable-next-line react-hooks/refs -- an event handler, built by a render helper; the save it calls reads refs when run, not during render
        onRemove={() => (draft ? setDependencyColumn(NO_DEPENDENCY) : handleRemoveTableDependency(index))}
        disabled={disabled}
      >
        {/* "and" from the second on, as the toolbar's filters join theirs. */}
        {(draft ? dependencies.length > 0 : index > 0) && ruleWord('and')}
        {/* The source's column first, as a filter's column is, then this row's column it matches. */}
        {tableOwnerSegment(selectedTableName)}
        {impliedJoin ? (
          <span className={`${STATIC_FIELD_SEGMENT} pl-1`}>
            <IconSelector kind={impliedJoin.kind} />
            <span className="min-w-0 truncate">{impliedJoin.name}</span>
          </span>
        ) : (
          <PanelSegmentSelect
            tone="field"
            ariaLabel={`Column in ${selectedTableName ?? 'the source'}`}
            placeholder="Choose a column"
            options={dependencyJoinOptions}
            selected={dependency.columnJoinId ? [dependency.columnJoinId] : []}
            // eslint-disable-next-line react-hooks/refs -- an event handler, built by a render helper; the save it calls reads refs when run, not during render
            onPick={(columnJoinId) => handleDependencyJoin(index, columnJoinId)}
            loading={!!viewId && selectedTableViewColumns === undefined}
            defaultOpen={draft}
            disabled={disabled}
            className="max-w-[160px] shrink-0 pl-1 font-medium"
          />
        )}
        <PanelSegmentSelect
          lead="matches"
          tone="field"
          ariaLabel={`Column in ${spreadsheetData.tableDetails.name}`}
          options={ownColumnOptions(dependency.columnId)}
          selected={dependency.columnId ? [dependency.columnId] : []}
          onPick={(columnId) => handleDependencyColumn(index, columnId)}
          disabled={disabled}
          className="max-w-[160px] shrink-0 font-medium"
        />
      </PanelRule>
    )
  }

  // Beside the source's settings in its dialog, the options they make, as the Options page lists them: a
  // search, the options in their groups, those missing from the source, and at the foot how many there are
  // and the source table to open.
  const previewEmpty = (): React.ReactNode => {
    if (sourceRestricted)
      return (
        <Empty
          icon={<Grid />}
          title="Source can't be opened"
          description="Its options aren't shown, as the table or view can't be opened with your access."
        />
      )
    if (!viewColumnId)
      return (
        <Empty
          icon={<Grid />}
          title="No column chosen"
          description="Choose the table, view and column the options come from, and they show here."
        />
      )
    if (sourceOptionsLoading) return <LoadingSourceOptions />
    if (linkedOptionCount === 0) return <NoSourceOptions />
    return null
  }
  const sourcePreview = (
    <>
      <div className="flex h-10 shrink-0 items-center gap-1 px-3 text-[13px] font-medium text-body select-none">
        {showingMissing ? (
          <>
            <Button
              internalType="transparent"
              aria-label="Back to the preview of options"
              className="-ml-1.5 size-6 p-0 text-muted hover:text-body"
              onClick={closeMissing}
            >
              <Triangle type="left" />
            </Button>
            Options missing from source table
            <span className="ml-auto flex">{missingSearchButton}</span>
          </>
        ) : (
          <>
            Preview of options
            {viewColumnId && !sourceRestricted && <span className="ml-auto flex">{missingSearchButton}</span>}
          </>
        )}
      </div>
      <div className="border-t border-divider">{linkedFilterRow(!viewColumnId || sourceRestricted, true)}</div>
      <div className="flex min-h-0 flex-1 flex-col gap-1 overflow-y-auto px-3 pt-2 pb-3 [&>*]:shrink-0">
        {previewEmpty() ?? linkedOptionsList(false)}
      </div>
      <div className={DIALOG_ASIDE_FOOTER}>
        {viewColumnId && !sourceRestricted && (
          <>
            {/* No count until the options it counts have loaded. */}
            {!sourceOptionsLoading && (
              <span className="flex items-center gap-1 pl-1.5 text-[13px] text-muted">
                {linkedCountLabel}
                {!showingMissing && missingLink(openMissing)}
              </span>
            )}
            {sourceTableLink}
          </>
        )}
      </div>
    </>
  )

  const sourceDialog = (
    <Dialog
      open={sourceDialogOpen}
      onOpenChange={(open) => {
        setSourceDialogOpen(open)
        // Its preview's search and choice are for that visit, as the Options page's are.
        if (open) return
        setOptionSearch('')
        setMissingShown(false)
        setSearchOpen(false)
      }}
      // Named for what opened it, and for the column, with its icon, so it's clear whose options these are.
      title={
        <>
          {sourceDialogLinked ? 'Update source for ' : 'Link a table to '}
          <span className="whitespace-nowrap">
            {kind && (
              <span className="mr-1.5 inline-flex align-[-2px] [&_svg]:size-4">
                <IconSelector kind={kind} />
              </span>
            )}
            {column?.name ?? 'this column'}
          </span>
        </>
      }
      width="split"
      nestedInMenu
      aside={sourcePreview}
      footer={
        <>
          <Button internalType="outline" className={BLUE_BUTTON} onClick={() => setSourceDialogOpen(false)}>
            Done
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-5">
        {/* The source, as Edit column shows it, in blue: opening its own small dialog to change it. */}
        <Field label="Source column" htmlFor="source-table-button" className="flex flex-col gap-1.5">
          <Button
            id="source-table-button"
            internalType="transparent"
            // On the light blue, the path in the text's own colour, the table and the pencil in blue.
            className="h-9 w-full justify-start gap-1.5 rounded-lg bg-really-light-blue px-3 text-[13px] font-medium text-body hover:bg-really-light-blue hover:text-body hover:brightness-[0.98] [&_svg]:size-3.5 [&_svg]:shrink-0"
            aria-haspopup="dialog"
            onClick={openSourcePicker}
            disabled={disabled}
          >
            <span className="flex text-blue">
              <Grid />
            </span>
            <span className="min-w-0 flex-1 truncate text-left">{sourceColumnButtonLabel()}</span>
            {/* Whether the rows' values follow the source, as the Source column dialog sets it: a state, not a
                button, in the text's colour (faint when off) and set apart from the pencil. */}
            {sourceLabel && (
              <Tooltip
                content={
                  currentKindOptions.tableOptions.liveValues
                    ? 'Values update when they change in the source'
                    : "Values don't update when they change in the source"
                }
              >
                <span
                  className={`mr-1.5 flex ${currentKindOptions.tableOptions.liveValues ? 'text-body' : 'text-faint'}`}
                  data-testid="source-sync-state"
                >
                  {currentKindOptions.tableOptions.liveValues ? <SyncValues /> : <SyncValuesOff />}
                </span>
              </Tooltip>
            )}
            {/* A pencil: it opens a dialog to change the source, not a list. */}
            <span className="flex text-blue">
              <Pencil />
            </span>
          </Button>
        </Field>

        <Dialog
          open={sourcePickerOpen}
          onOpenChange={setSourcePickerOpen}
          title="Source column"
          description={
            <>
              Select a column from which to source options for <ColumnName column={column} />.
            </>
          }
          nestedInMenu
          footer={
            <>
              {/* Unlinking, set apart from Save: the column keeps its values, and goes back to choosing a source. */}
              {tableId && (
                <div className="mr-auto -ml-2">
                  <PanelAction
                    destructive
                    onClick={() => {
                      handleRemoveTableOptions()
                      setSourcePickerOpen(false)
                    }}
                    disabled={disabled}
                  >
                    Remove link
                  </PanelAction>
                </div>
              )}
              <Button internalType="outline" onClick={() => setSourcePickerOpen(false)}>
                Cancel
              </Button>
              <Button
                internalType="outline"
                className={BLUE_BUTTON}
                disabled={
                  !sourceDraft.columnId ||
                  !draftSourceView ||
                  (sourceDraft.tableId === tableId &&
                    sourceDraft.viewId === viewId &&
                    sourceDraft.columnId === viewColumnId &&
                    sourceDraft.liveValues === !!currentKindOptions.tableOptions.liveValues)
                }
                onClick={handleSaveSource}
              >
                Save
              </Button>
            </>
          }
        >
          <div className="flex flex-col gap-3">
            <SelectSourceFields
              tableId={sourceDraft.tableId}
              viewId={sourceDraft.viewId}
              columnId={sourceDraft.columnId}
              onTableChange={(draftTableId) =>
                setSourceDraft((draft) => ({ ...draft, tableId: draftTableId, viewId: '', columnId: '' }))
              }
              onViewChange={(draftViewId) =>
                setSourceDraft((draft) => ({ ...draft, viewId: draftViewId, columnId: '' }))
              }
              onColumnChange={(draftColumnId) => setSourceDraft((draft) => ({ ...draft, columnId: draftColumnId }))}
              onViewLoaded={setDraftSourceView}
              disabled={disabled}
            />
            {/* How the rows keep up with the source, set with the source itself. */}
            <SwitchField
              // 16px either side of its line, as the dialog's own fields are apart.
              className="mt-1"
              title="Update values when they change in the source"
              description="When a value is renamed in the source column, the rows that use it are renamed too."
              checked={sourceDraft.liveValues}
              onChange={(liveValues) => setSourceDraft((draft) => ({ ...draft, liveValues }))}
              disabled={disabled}
            />
            {dependencies.length > 0 && (sourceDraft.tableId !== tableId || sourceDraft.viewId !== viewId) && (
              <p className="text-muted">
                Another table or view removes the{' '}
                {dependencies.length === 1 ? 'dependency' : `${dependencies.length} dependencies`} set up on this one.
              </p>
            )}
          </div>
        </Dialog>

        {sourceRestricted && (
          <p className="text-red">
            The table or view chosen by another admin of this table cannot be opened with your access.
            {tableId && viewId && (
              <>
                {' '}
                <Link to={sourceViewPath} target="_blank" rel="noopener noreferrer">
                  Go to the table
                </Link>{' '}
                to request access.
              </>
            )}
          </p>
        )}

        {viewColumnId && !sourceRestricted && (
          // As the toolbar's filters are written: a rule to a pill, "⊙ Region matches ⊙ Regional availability",
          // each with its ×, and the ghost "+ Add dependency" under them.
          <section className="flex flex-col border-t border-divider pt-4">
            {/* With the blue icon the preview's "When …" headings carry, so the two read as the same thing. */}
            {/* Set as the Source column label over it is, with a little more room under it. */}
            <span className="mb-1.5 flex items-center gap-1.5 text-[13px] leading-5 font-medium text-body">
              <span className="flex text-blue [&_svg]:size-4">
                <Dependency />
              </span>
              Dependencies
            </span>
            {/* With rules, the sentence they finish, from the source: "Show corresponding ⊞ › ⊙ Plan name where:
                …";
                without, what they would do. */}
            {dependencies.length > 0 || dependencyColumn.columnId ? (
              <span className="text-muted">
                Show corresponding{' '}
                {/* The table as its blue icon alone, as the rules show it, its name on hover: the Source
                    column above names it in full. */}
                <Tooltip content={selectedTableName ?? 'The source table'}>
                  <span
                    className="ml-1 inline-flex align-[-2px] text-blue [&_svg]:size-3.5"
                    aria-label={selectedTableName}
                  >
                    <Grid />
                  </span>
                </Tooltip>
                <span className="mx-1 text-faint">›</span>
                <span className="font-medium whitespace-nowrap text-body">
                  {sourceColumnKind && (
                    <span className="mr-1 inline-flex align-[-2px] [&_svg]:size-3.5">
                      <IconSelector kind={sourceColumnKind} />
                    </span>
                  )}
                  {sourceColumnLabel}
                </span>{' '}
                where:
              </span>
            ) : (
              <span className="text-muted">
                Limit each row&apos;s options to source rows that match one of its columns.
              </span>
            )}
            <div className="-mx-3">
              {(dependencies.length > 0 || dependencyColumn.columnId) && (
                <PanelRules
                  ids={[
                    ...dependencies.map((_, index) => `dependency-${index}`),
                    ...(dependencyColumn.columnId ? ['dependency-new'] : [])
                  ]}
                >
                  {dependencies.map((dependency, index) => dependencyRule(dependency, index))}
                  {dependencyColumn.columnId && dependencyRule(dependencyColumn, -1)}
                </PanelRules>
              )}
              <PanelAdd
                label="Add dependency"
                options={dependencyColumnOptions}
                onPick={handleStartDependency}
                disabled={disabled}
                noOptionsMessage="No other columns can be a dependency."
              />
            </div>
            {/* Only of use with dependencies: they're what can leave a row a single option. It acts as a row is
                added, so it says so, naming the columns the rules match on. */}
            {(dependencies.length > 0 || currentKindOptions.autopopulate) && (
              <SwitchField
                boxed
                title="Auto-select unique matches"
                description={
                  <>
                    Automatically select an option for <ColumnName column={column} /> if only one matches all
                    dependencies.
                  </>
                }
                checked={currentKindOptions.autopopulate}
                onChange={handleSetAutopopulateValues}
              />
            )}
          </section>
        )}
      </div>
    </Dialog>
  )

  const settingsColumn = (
    <>
      <Field label="Name" htmlFor="column-name">
        {/* Medium, as the Type dropdown's choice under it is. */}
        <Input
          id="column-name"
          ref={nameInputRef}
          className="h-8 rounded-md font-medium"
          type="text"
          value={rename.draftName}
          readOnly={isSynced}
          onChange={(event) => rename.setDraftName(event.target.value)}
          onBlur={(event) => rename.commit(event.target.value)}
          onKeyDown={(event) => {
            if (event.key !== 'Enter') return
            event.preventDefault()
            // For a new column, Enter adds it, as Add column does.
            if (newColumn) {
              handleCreate()
              return
            }
            rename.commit(event.currentTarget.value)
          }}
        />
        {rename.error ? (
          <div data-testid="column-name-error" className="text-red whitespace-normal">
            {rename.error}
          </div>
        ) : null}
      </Field>

      <Field label="Type" htmlFor="column-type">
        <div className="w-full block" data-tour="column-type-selector">
          <Select
            inputId="column-type"
            options={[...ALL_TYPES]
              .sort((typeA, typeB) => COLUMN_TYPE_NAMES[typeA].localeCompare(COLUMN_TYPE_NAMES[typeB]))
              .map((type) => ({
                label: COLUMN_TYPE_NAMES[type],
                value: type as string,
                icon: <IconSelector kind={type} />
              }))}
            size="sm"
            disabled={disabled || isSynced}
            optionsSelected={column ? [column.kind] : []}
            onOptionClick={(option) => handleOnKindChange(option)}
          />
        </div>
      </Field>

      {kind && <ColumnTypeFormattingFields kind={kind} value={configValue} onChange={handleConfigChange} />}

      {/* The two settings most columns don't need, under the name and type rather than tied to either. */}
      <ColumnDescriptionField
        key={publicId}
        description={description}
        editorId={publicId ? publicId : ''}
        tablePublicId={spreadsheetData.tableDetails.publicId}
        onChange={handleDescriptionChange}
      />

      {/* Right under Add description, nothing between them: the two rows read as the one list. */}
      {showDefaultValue && !defaultShown && (
        <MenuList className="pt-0">
          <MenuItem
            icon={<Plus />}
            data-testid="add-default-value"
            data-tour="column-add-default-value"
            onClick={() => setDefaultShown(true)}
          >
            Add default value
          </MenuItem>
        </MenuList>
      )}

      {showDefaultValue && defaultShown && (
        <div data-tour="column-default-value">
          <Field
            label={
              <span className="inline-flex items-center gap-1">
                Default value
                {defaultValueError && (
                  <Tooltip content={`${defaultValueError}. It won't be applied to new rows.`}>
                    <span
                      className="inline-flex"
                      data-testid="default-value-error"
                      aria-label={`${defaultValueError}. It won't be applied to new rows.`}
                    >
                      <ErrorOutline className="size-4 text-red" />
                    </span>
                  </Tooltip>
                )}
              </span>
            }
            htmlFor="column-default-value"
          >
            {defaultValueControl()}
          </Field>
        </div>
      )}

      {showSelectOptions && (
        <div className="group/options mt-3 border-t border-divider">
          <div className="flex h-10 items-center gap-2 px-3">
            <span className="text-[13px] font-medium text-body select-none">Options</span>
            {/* Sorting the list: there when pointing at the options, out of the way otherwise. */}
            {optionsMode === 'manual' &&
              manualOptions.length > 1 &&
              sortButton(
                'size-6 opacity-0 transition-opacity group-hover/options:opacity-100 focus-visible:opacity-100 [@media(hover:none)]:opacity-100 [&_svg]:size-3.5'
              )}
            <div className="-mr-1.5 ml-auto flex min-w-0 items-center">{optionsSourceControl}</div>
          </div>

          {optionsMode === 'manual' && manualOptionsPane}

          {/* Linking a table and leaving before choosing what to link: say what's missing, and offer a way out. */}
          {optionsMode === 'table' && !viewColumnId && !sourceRestricted && missingSource('mx-3 mb-1.5')}

          {/* Said whenever the source has nothing to offer, even with options of the column's own still listed as
              missing from it (a manual list since linked to an empty column). */}
          {optionsMode === 'table' && viewColumnId && !sourceRestricted && sourceOptionsLoading && (
            <LoadingSourceOptions className="mx-3 mb-1.5" />
          )}

          {optionsMode === 'table' &&
            viewColumnId &&
            !sourceRestricted &&
            !sourceOptionsLoading &&
            sourcedOptions.length === 0 && (
              <NoSourceOptions className="mx-3 mb-1.5" data-testid="options-source-empty">
                <Link
                  to={sourceViewPath}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex h-8 items-center gap-1.5 rounded-lg border border-divider bg-surface-raised px-3 font-medium text-body hover:bg-surface-hover [&_svg]:size-3.5 [&_svg]:text-muted"
                >
                  Open source table
                  <ExternalLink />
                </Link>
              </NoSourceOptions>
            )}

          {optionsMode === 'table' &&
            viewColumnId &&
            !sourceRestricted &&
            !sourceOptionsLoading &&
            sourcedOptions.length > 0 && (
              // The source's first five, fading out after them under a pill that opens them all in the panel.
              <div className="relative flex flex-col gap-1 px-3 pb-1.5">
                <div
                  className="flex flex-col gap-1"
                  style={
                    sourcedOptions.length > PREVIEWED_OPTIONS
                      ? { maskImage: 'linear-gradient(to bottom, black 45%, transparent)' }
                      : undefined
                  }
                >
                  {optionGroupsList(groupedOptions(sourcedOptions.slice(0, PREVIEWED_OPTIONS)))}
                </div>
                {sourcedOptions.length > PREVIEWED_OPTIONS && (
                  <ViewAllPill
                    count={sourcedOptions.length}
                    className="bottom-3"
                    onClick={() => {
                      setOptionSearch('')
                      setSubPanel('options')
                    }}
                  />
                )}
              </div>
            )}
        </div>
      )}

      {!isReadOnly && !isNew && (
        // Its own section, as Options and Data validation are: the formula, if there's one, and the button
        // that adds or edits it.
        <div className="mt-3 border-t border-divider">
          <div className="flex h-10 items-center px-3 text-[13px] font-medium text-body select-none">Formula</div>
          {formulaEnabled && formula && (
            <div className="mx-3 mb-2 truncate rounded-lg border border-divider bg-surface-sunken px-3 py-2 font-mono text-[12px] text-body">
              {formula}
            </div>
          )}
          <div className="flex items-center gap-1 px-3 pb-1.5">
            <Button
              internalType="outline"
              className="h-8 gap-1.5 px-2.5 text-[13px] font-medium [&_svg]:size-4 [&_svg]:text-muted"
              data-testid="column-formula-button"
              onClick={() => setSubPanel('formula')}
            >
              {formulaEnabled ? <Pencil /> : <Plus />}
              {formulaEnabled ? 'Edit formula' : 'Add formula'}
            </Button>
            {scriptsAllowed && (
              <div className="-mr-2 ml-auto">
                <PanelAction icon={<Code />} onClick={() => setSubPanel('script')}>
                  {scriptEnabled ? 'Edit script' : 'Script'}
                </PanelAction>
              </div>
            )}
          </div>
        </div>
      )}

      {isJoined && !isNew && (
        // Where a lookup column's values come from, as Options shows a linked select's: the source in blue at the
        // heading's right, opening the lookups there, and a sample of the values matched and those the column
        // takes from them.
        <div className="mt-3 border-t border-divider" data-testid="edit-column-lookup">
          <div className="flex h-10 items-center gap-2 px-3">
            <span className="text-[13px] font-medium text-body select-none">Lookup</span>
            {columnLookup && (
              <div className="-mr-1.5 ml-auto flex min-w-0 items-center">
                <Tooltip content="Edit lookup">
                  <Button
                    internalType="transparent"
                    aria-label={`Edit lookup from ${columnLookup.joinTableName}`}
                    className="h-7 min-w-0 shrink gap-1.5 rounded-md px-1.5 text-[13px] font-medium text-blue hover:bg-really-light-blue hover:text-blue [&_svg]:size-3.5 [&_svg]:shrink-0"
                    onClick={openColumnLookups}
                    data-testid="edit-column-lookup-source"
                  >
                    <Grid />
                    <span className="min-w-0 truncate">{columnLookup.joinTableName}</span>
                    <Pencil />
                  </Button>
                </Tooltip>
              </div>
            )}
          </div>
          {columnLookup && publicId && (
            <LookupSample join={columnLookup} columnId={publicId} onViewAll={openColumnLookups} />
          )}
        </div>
      )}

      {kind && (
        // A hairline and a heading, as Options has, over the validation's fields and switches.
        <div className="mt-1.5 border-t border-divider">
          {/* Its rows bring their own padding, so the heading leaves less below it: about the gap from Options
              to its first option. */}
          <div className="px-3 pt-2.5 pb-1.5 text-[13px] leading-5 font-medium text-body select-none">
            Data validation
          </div>
          <ColumnValidationFields kind={kind} value={configValue} onChange={handleConfigChange} />
          {/* A linked column's own options the source doesn't have, under the rules they break: a quiet grey line
              saying how many, what it means on hover, and the list of them on a click. */}
          {optionsMode === 'table' && viewColumnId && notInSource.length > 0 && (
            // Inset as the menus' search is: its icon and text lined up with the rows' above, at their weight.
            <div className="px-1 pt-1 pb-2">
              <Button
                internalType="transparent"
                data-testid="options-missing-banner"
                aria-label={`${missingTitle}: review them`}
                className="h-8 w-full justify-start gap-2 rounded-md border-0 bg-surface-sunken px-2 text-[13px] font-medium text-body hover:bg-surface-hover [&_svg]:shrink-0"
                onClick={() => {
                  setOptionSearch('')
                  setMissingShown(true)
                  setSubPanel('options')
                }}
              >
                <Warning />
                <span className="min-w-0 flex-1 truncate text-left">
                  <HoverCard title={missingTitle} content={missingSentence}>
                    {missingTitle}
                  </HoverCard>
                </span>
                <span className="flex text-muted [&_svg]:size-3.5">
                  <Triangle type="right" />
                </span>
              </Button>
            </div>
          )}
        </div>
      )}
    </>
  )

  const wideSubPanel = subPanel !== null && WIDE_SUB_PANELS.includes(subPanel)

  return (
    <FloatingMenu
      id={id}
      menuState={menuState}
      setMenuState={setMenuState}
      width={wideSubPanel ? SUB_PANEL_WIDTH : baseWidth}
      fullScreen={wideSubPanel && subPanelFullScreen}
      zIndex={3000}
      shouldBlockClose={formulaHasChanges}
      confirmMessage="You have an unsaved formula. Are you sure you want to close and discard your changes?"
      // Closing (its ×, Esc) unmounts the Name field before it can blur — so closing has to commit
      // the pending rename here or it would be lost, along with any setting still waiting out its
      // debounce.
      onClose={handleClose}
      // Named for the column, so a panel moved aside still says what it's editing.
      title={
        <>
          {isNew ? 'Adding' : 'Editing'}
          {/* The type's icon goes with the name, set as the column's header sets them, apart from "Editing". */}
          <span className="ml-2 font-semibold text-body">
            <span className="mr-1 inline-flex align-[-2px] [&_svg]:size-3.5 [&_svg]:stroke-[2.25]">
              {kind && <IconSelector kind={kind} />}
            </span>
            {column?.name ?? 'column'}
          </span>
        </>
      }
    >
      <div className={panel({ view: panelView(subPanel, subPanelFullScreen) })}>
        {!subPanel && (
          <div className="flex-1 overflow-y-auto overflow-x-hidden min-h-0 whitespace-normal pb-1">
            {settingsColumn}
          </div>
        )}

        {/* A new column is made with what's set here once it's added; until then nothing is saved. */}
        {newColumn && !subPanel && (
          <PanelFooter>
            <div className="ml-auto flex items-center gap-1.5">
              <Button internalType="outline" onClick={newColumn.onCancel} disabled={newColumn.creating}>
                Cancel
              </Button>
              <Button
                internalType="outline"
                className={BLUE_BUTTON}
                data-testid="add-column-submit"
                isLoading={newColumn.creating}
                onClick={handleCreate}
              >
                Add column
              </Button>
            </div>
          </PanelFooter>
        )}

        {subPanel && (
          <div className="flex flex-col min-h-0 flex-1 min-w-0">
            {/* As the Options heading on the main page is set: 13px medium, in a shorter bar. */}
            <div className="flex h-9 items-center gap-1 border-b border-divider pr-3 pl-1.5 shrink-0">
              <Button
                internalType="transparent"
                type="button"
                // From the missing values, back to the options they were opened from.
                aria-label={subPanel === 'options' && showingMissing ? 'Back to options' : 'Back to column settings'}
                className="size-6 p-0 text-muted hover:text-body focus-visible:outline-2 focus-visible:outline-blue"
                onClick={subPanel === 'options' && showingMissing ? closeMissing : closeSubPanel}
              >
                <Triangle type="left" />
              </Button>
              <span className="shrink-0 text-[13px] leading-5 font-medium whitespace-nowrap text-body select-none">
                {subPanel === 'options' && showingMissing
                  ? 'Options missing from source table'
                  : SUB_PANEL_TITLES[subPanel]}
              </span>
              <div className="ml-auto flex min-w-0 items-center gap-2">
                {subPanel === 'options' && !showingMissing && (
                  <div className="-mr-1.5 flex min-w-0">{optionsSourceControl}</div>
                )}
                {subPanel === 'options' && showingMissing && <div className="-mr-1.5 flex">{missingSearchButton}</div>}
                {subPanel === 'script' && (
                  <Button
                    className="w-[100px]"
                    internalType={scriptEnabled ? 'danger' : 'accept'}
                    disabled={isSynced}
                    onClick={() => {
                      handleScriptEnabledChange(!scriptEnabled)
                      if (scriptEnabled) setSubPanel(null)
                    }}
                  >
                    {scriptEnabled ? 'Disable' : 'Enable'}
                  </Button>
                )}
                {wideSubPanel && (
                  <Tooltip content={subPanelFullScreen ? 'Exit full screen' : 'View full screen'} className="w-max">
                    <Button
                      internalType="transparent"
                      type="button"
                      aria-pressed={subPanelFullScreen}
                      aria-label={subPanelFullScreen ? 'Exit full screen' : 'View full screen'}
                      className="h-auto flex items-center text-muted hover:text-body rounded p-0.5 focus-visible:outline-2 focus-visible:outline-blue"
                      onClick={() => setSubPanelFullScreen((full) => !full)}
                    >
                      {subPanelFullScreen ? <Cross /> : <Expand />}
                    </Button>
                  </Tooltip>
                )}
              </div>
            </div>

            <div className="flex-1 min-h-0 flex flex-col overflow-hidden">
              {subPanel === 'options' && optionsMode === 'manual' && (
                // Every hand-entered option, to type into, reorder and remove, and Add option under them.
                <>
                  {/* The menus' search, as a linked table's page has it, and the list's sort to its right. */}
                  {/* 8px above and below the search, between the header's line and its own. */}
                  <div className="flex items-start gap-0.5 border-b border-divider px-2 pt-1 pb-2">
                    <div className="min-w-0 flex-1">
                      <MenuSearch
                        aria-label="Search the options"
                        placeholder="Search options"
                        value={optionSearch}
                        onChange={(event) => setOptionSearch(event.target.value)}
                      />
                    </div>
                    {manualOptions.length > 1 && sortButton('mt-1 mr-1 size-8 shrink-0 [&_svg]:size-4')}
                  </div>
                  <div className="min-h-0 flex-1 overflow-y-auto pt-2 pb-1.5">
                    {manualOptionsList(false)}
                    {optionSearch && manualMatchCount === 0 && (
                      <Empty
                        className="mx-3"
                        icon={<Search />}
                        title="No options match"
                        description="Try another search, or add it as an option."
                      />
                    )}
                  </div>
                  <PanelFooter>
                    <span className="px-1.5 text-[13px] leading-7 text-muted">
                      {countLabel(manualMatchCount, manualOptions.length)}
                    </span>
                  </PanelFooter>
                </>
              )}

              {subPanel === 'options' && optionsMode === 'table' && !viewColumnId && !sourceRestricted && (
                <div className="min-h-0 flex-1 overflow-y-auto p-3">{missingSource('')}</div>
              )}

              {subPanel === 'options' && optionsMode === 'table' && (viewColumnId || sourceRestricted) && (
                // Every option, as the panel lists them, with a search for a long list, and the source to open.
                <div className="flex min-h-0 flex-1 flex-col">
                  {linkedFilterRow()}
                  {/* Its rows kept their height however long the list: none of them shrinks to fit. */}
                  <div className="flex min-h-0 flex-1 flex-col gap-1 overflow-y-auto px-3 pt-2 pb-3 [&>*]:shrink-0">
                    {linkedOptionsList(true)}
                  </div>
                  {/* How many there are (of how many, while searching), and the table itself, to change what's in it,
                      on the menus' grey band at the foot. */}
                  <PanelFooter>
                    {!sourceOptionsLoading && (
                      <span className="flex items-center gap-1 pl-1.5 text-[13px] leading-7 text-muted">
                        {linkedCountLabel}
                        {!showingMissing && missingLink(openMissing)}
                      </span>
                    )}
                    {sourceTableLink}
                  </PanelFooter>
                </div>
              )}

              {subPanel === 'formula' && column && (
                <Formula
                  columnPublicId={column.publicId}
                  formula={formula ? formula : DEFAULT_FORMULA}
                  setEditMenu={setMenuState}
                  setHasChanges={setFormulaHasChanges}
                />
              )}

              {subPanel === 'script' && column && scriptEnabled && (
                <div className="flex-1 min-h-0 overflow-y-auto p-2.5">
                  <Script
                    columnPublicId={column.publicId}
                    script={script}
                    handleUpdateColumnError={handleUpdateColumnError}
                  />
                </div>
              )}
            </div>
          </div>
        )}
      </div>
      {sourceDialog}
      {/* Here rather than with the Options heading, so the Options page's source button opens it too. */}
      {sourceMenu.open && (
        <Menu
          id="column-source-actions"
          nested
          menuState={sourceMenu}
          setMenuState={setSourceMenu}
          width={SOURCE_MENU_WIDTH}
          zIndex={OVER_PANEL_Z_INDEX}
        >
          <MenuList>
            <MenuItem
              icon={<UpdateSource />}
              onClick={() => {
                setSourceMenu(INITIAL_CONTEXT_MENU_STATE)
                openSourceDialog()
              }}
            >
              Update source
            </MenuItem>
            <MenuItem
              icon={<ManualList />}
              onClick={() => {
                setSourceMenu(INITIAL_CONTEXT_MENU_STATE)
                switchToManualList()
              }}
            >
              Switch to manual list
            </MenuItem>
            {sourceViewPath && (
              <>
                <MenuSeparator />
                <MenuItem
                  icon={<ExternalLink />}
                  to={sourceViewPath}
                  newTab
                  onClick={() => setSourceMenu(INITIAL_CONTEXT_MENU_STATE)}
                >
                  Open source table
                </MenuItem>
              </>
            )}
          </MenuList>
        </Menu>
      )}
      {confirmManualList && (
        <ActionDialog
          nestedInMenu
          title="Switch to a manual list?"
          description={`The options will no longer come from ${selectedTableName ?? 'the source table'}. Options typed into this column's list stay.`}
          confirmLabel="Switch to manual list"
          onClose={() => setConfirmManualList(false)}
          onConfirm={async () => {
            handleRemoveTableOptions()
            setOptionsMode('manual')
          }}
        />
      )}
      {changeColumnKindModalState && selectedKind && (
        <ColumnKindModal
          id="change-column-kind-modal"
          open={changeColumnKindModalState}
          setOpen={setChangeColumnKindModalState}
          newKind={selectedKind}
          columnId={publicId}
          onCancel={() => {
            if (column) {
              setSelectedKind(column.kind)
            }
          }}
          zIndex={3001}
        />
      )}
    </FloatingMenu>
  )
}

export default EditColumnMenu
